import React, { useEffect,useCallback, useMemo, useState } from "react";
import { HiOutlineChevronDown } from "react-icons/hi2";
import BreakdownIcon from "assets/timesheet/breakdownIcon.svg";
import { TimesheetCodeAllocationBreakdownIcon } from "../../../components/TimesheetRevamp/TimesheetCodeAllocationLayout";
import SelectOption from "components/common/Select/Select";
import Select from "react-select";
import moment from "moment";
import {
  calculateTotalHoursAndMinutes,
  capitalizeFirstLetter,
  capitalizeWords,
  convertTimeToDatetimeLocalWithoutMoment,
  customFormatDate,
  embedDateWithTime,
  extractTimeFromDateTime,
  getShiftLength,
  isCheckNextDayShift,
  isEndTimeNextDay,
  parseBooleanVal,
} from "Utils/utils";
import {
  ADVANCE_SEGMENT_PARTICIPANT_OVERLAP_MSG,
  getAdvanceSegmentWindowMinutes,
  isAdvanceSameClockTimeFullDay,
  isAdvanceShiftZeroDuration,
  validateAdvanceSegmentEndTime,
  validateAdvanceSegmentNoOverlap,
  validateAdvanceSegmentStartTime,
  validateAdvanceSplitEndTime,
} from "pages/Rostering/WorkerShift/components/advanceModeValidation";
import { getBasicShiftDurationHHMM } from "pages/Rostering/WorkerShift/components/basicModeValidation";
import {
  ADVANCE_TIME_INPUT_CLASS,
  AdvanceModeFieldError,
  AdvanceModeTimeRange,
  AdvanceModeTimeRangeSharedError,
  AdvanceModeSplitSegmentButton,
  AdvanceModeParticipantRemoveButton,
} from "pages/Rostering/WorkerShift/components/advanceModeUi";
import "pages/Rostering/WorkerShift/components/advanceModeTimePicker.css";
import InputField from "components/common/Input/Input";
import { Controller } from "react-hook-form";
import ToggleSwitch from "components/common/NewSwitch";
import ActiveHours from "pages/Rostering/TimeSheet/TimeSheetTabs/TimesheetBreakdown/ActiveHours";
import { toast } from "react-toastify";
import { apiPostRequest, apiGetRequest } from "../../../../../../helpers/Requests";
import { URL_ROSTER_LINK_TO_CORE_FUNDING } from "../../../../../../store/services/URL";
import { CORE_CATEGORIES, getLineItemLabel, getParticipantLabel } from "../../../../../../Constants/constant";
import {
  DEFAULT_ADVANCE_RATIO,
  buildNdisSelectedOption,
  extractRatioFromLineItemLabel,
  formatShiftDateForNdisApi,
  DEFAULT_ADVANCE_CLAIM_TYPE,
  getBreakdownClaimType,
  isTravelFromClaimType,
  mapNdisOptionsForClaimType,
  mapShiftNdisItemsToOptions,
} from "pages/Rostering/WorkerShift/components/advanceModeSplitDefaults";
import AdvanceModeRatioSelect from "pages/Rostering/WorkerShift/components/AdvanceModeRatioSelect";
import {
  calculateAppliedPerParticipantRate,
  formatBillingDurationLabel,
  getBillingParticipantsForRow,
  getBreakdownParticipantKey,
  isV5MultiAssignmentTimesheet,
  mergeBreakdownSavedLineItemOption,
  resolveAdvanceInvoiceCategoryName,
  formatBreakdownScheduleHeader,
  resolveBreakdownScheduleTimes,
  resolveBreakdownDefaultRatio,
  toBreakdownNdisSelectionShape,
} from "./v5BillingUtils";
import {
  fetchShiftNdisLineItems,
  fetchTimesheetNdisLineItems,
  invalidateShiftNdisLineItemsCache,
  invalidateTimesheetNdisLineItemsCache,
} from "./ndisLineItemsFetchCache";
import { computeAppliedRateFromBillRate } from "../../../utils/codeAllocationRates";
import {
  breakdownItemUsesAlternateRate,
  resolvePersistedBreakdownUnitRate,
} from "../../../utils/breakdownFormHydration";

/** Pull support-item number from "Name - CODE" labels when option.number is missing. */
const parseNumberFromLineItemLabel = (label) => {
  if (!label || typeof label !== "string") return "";
  const idx = label.lastIndexOf(" - ");
  if (idx === -1) return "";
  return label.slice(idx + 3).trim();
};

const resolveBreakdownSupportItemNumber = (option, row) => {
  const direct =
    (option && typeof option === "object"
      ? option.number || option.support_item_number
      : "") ||
    row?.ndis_support_item_number ||
    row?.support_item_number ||
    row?.item_numbers?.selected?.support_item_number ||
    "";
  if (direct) return String(direct);

  return (
    parseNumberFromLineItemLabel(option?.label) ||
    parseNumberFromLineItemLabel(option?.name) ||
    parseNumberFromLineItemLabel(
      row?.ndis_support_item_name || row?.support_item_name
    ) ||
    ""
  );
};

const NDIS_ITEMS_FETCH_DEBOUNCE_MS = 600;

const isSleepoverServiceCategoryName = (name) =>
  name === "Self-Care (Deprecated)" ||
  name === "Home and Living" ||
  name === "Assistance with Daily Life";


function isSleepoverBreakdownTimeRange(startStr, endStr) {
  if (!startStr || !endStr) return false;
  const start = moment(startStr, "HH:mm", true);
  const endSameDay = moment(endStr, "HH:mm", true);
  if (!start.isValid() || !endSameDay.isValid()) return false;
  if (!endSameDay.isBefore(start)) return false;
  const end = endSameDay.clone().add(1, "day");
  const startOk = start.hour() >= 22;
  const endH = end.hour();
  const endM = end.minute();
  const morningOk = endH < 6 || (endH === 6 && endM === 0);
  return startOk && morningOk;
}

const BREAKDOWN_CLOCK_FORMATS = ["HH:mm", "HH:mm:ss", "hh:mm A", "h:mm A"];

const datePartFromDateTime = (dateTime) => {
  if (!dateTime || typeof dateTime !== "string") return null;
  const part = dateTime.includes("T")
    ? dateTime.split("T")[0]
    : dateTime.split(" ")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(part) ? part : null;
};

const isMidnightClock = (timeValue) => {
  if (!timeValue) return false;
  const parsed = moment(timeValue, BREAKDOWN_CLOCK_FORMATS, true);
  return parsed.isValid() && parsed.hours() === 0 && parsed.minutes() === 0;
};

/** 10:00 PM–12:00 AM is still the start calendar day; 12:00 AM–12:00 AM is a 24h next-day window. */
const isExclusiveMidnightEnd = (startClock, endClock) =>
  isMidnightClock(endClock) && !isMidnightClock(startClock);

const clocksMatchDateTime = (clock, dateTime) => {
  if (!clock || !dateTime) return false;
  return extractTimeFromDateTime(dateTime) === clock;
};

export default function Accordion({
  timesheetBreakdown,
  setValue,
  control,
  item,
  index,
  watchFormData,
  shiftDetails,
  billingSplitMeta,
  isSplitBillingMode = false,
  isAdvanceMode = false,
  serviceTypes = [],

  catalogState,
  serviceCategory,
  errors,
  totalBreakDown,
  totalItemsDuration,
  register,
  clearErrors,
  suggestedItemNumber,
  isNonBillable,
  spSettings,
  disabledAllComponent,
  itemNumber = [],
  isDisabled,
  isSingleTimesheetBreakdowns = false,
  startTime,
  endTime,
  showClaimTypeFromBusinessSettings = false,
  showTravelFromBusinessSettings = false,
  localBreakdownIndex = null,
  localBreakdownTotal = null,
  shiftWindowStart = null,
  shiftWindowEnd = null,
  serviceCategoryId = null,
  allowSplit = false,
  canRemove = false,
  onSplit = null,
  onRemove = null,
}) {


  const lineItemLabel = getLineItemLabel(spSettings?.care_sector) || "Support item";
  const participantLabelPlural = getParticipantLabel(spSettings?.care_sector, true);

  const isStaShift = useMemo(() => {
    // Prefer shift-level flag so STA NDIS items load even when a line's is_sta is false/unset.
    const raw =
      shiftDetails?.is_sta ?? shiftDetails?.sta ?? item?.is_sta ?? false;
    return (
      parseBooleanVal(raw) || raw === 1 || String(raw) === "1"
    );
  }, [shiftDetails?.is_sta, shiftDetails?.sta, item?.is_sta]);
  const legacySplitShiftOnly = Boolean(
    shiftDetails?.is_split_shift && !isAdvanceMode
  );
  const useShiftWindowBounds = Boolean(
    shiftWindowStart &&
      shiftWindowEnd &&
      (isAdvanceMode || isSplitBillingMode || watchFormData?.invoice_breakdown)
  );
  // Prefer claim_type (API overlay) with is_travel fallback — billing-allocation
  // returns claim_type only, not is_travel. Also match parent invoice line when
  // local/preview breakdown rows omit claim_type.
  const breakdownClaimType = useMemo(() => {
    const fromItem = getBreakdownClaimType(item);
    if (fromItem) return fromItem;
    const lines = shiftDetails?.invoiceTimesheets;
    if (!Array.isArray(lines) || !lines.length) return null;
    const matched = lines.find(
      (line) =>
        (item?.uuid != null && String(line?.uuid) === String(item.uuid)) ||
        (item?.id != null && String(line?.id) === String(item.id)) ||
        (item?.split_shift_id != null &&
          String(line?.split_shift_id) === String(item.split_shift_id))
    );
    return getBreakdownClaimType(matched);
  }, [
    item?.claim_type,
    item?.is_travel,
    item?.uuid,
    item?.id,
    item?.split_shift_id,
    shiftDetails?.invoiceTimesheets,
  ]);
  const isTravelBreakdown = useMemo(
    () => isTravelFromClaimType(breakdownClaimType),
    [breakdownClaimType]
  );
  // Show Travel / NF2F (etc.) on the breakdown header; hide default STAND.
  const breakdownClaimTypeBadge = useMemo(() => {
    if (
      !breakdownClaimType ||
      breakdownClaimType === DEFAULT_ADVANCE_CLAIM_TYPE
    ) {
      return null;
    }
    return breakdownClaimType;
  }, [breakdownClaimType]);
  const billingParticipants = useMemo(
    () => getBillingParticipantsForRow(shiftDetails, index, item),
    [
      shiftDetails,
      index,
      item?.id,
      item?.uuid,
      item?._clientKey,
      item?.participants,
      item?.billingSplitMeta?.participants,
      item?.split_shift_id,
    ]
  );
  const isV5 = isV5MultiAssignmentTimesheet(shiftDetails);
  const [advanceItemNumbers, setAdvanceItemNumbers] = useState([]);
  const [advanceItemLoading, setAdvanceItemLoading] = useState(false);
  // Basic split Sleepover + Active Hours only. Never used by Advance or basic non-split.
  const [basicSplitActiveHoursItemNumbers, setBasicSplitActiveHoursItemNumbers] =
    useState([]);

  const breakdownServiceCategory = useMemo(() => {
    // Per-segment category for advance + basic split; parent category for standard basic.
    if (!isAdvanceMode && !legacySplitShiftOnly) return serviceCategory;

    const id =
      watchFormData[`invoice_service_type_id_${index}`] ??
      item?.service_type_id ??
      item?.invoice_service_type_id;
    const found = serviceTypes?.find(
      (s) => id != null && String(s.value) === String(id)
    );
    if (found) return found;
    if (item?.service_type_name) {
      return {
        name: item.service_type_name,
        label: item.service_type_name,
        value: id,
      };
    }
    return serviceCategory ?? null;
  }, [
    isAdvanceMode,
    legacySplitShiftOnly,
    serviceCategory,
    serviceTypes,
    watchFormData[`invoice_service_type_id_${index}`],
    index,
    item?.service_type_id,
    item?.invoice_service_type_id,
    item?.service_type_name,
  ]);

  const advanceCategoryItemName = useMemo(
    () => resolveAdvanceInvoiceCategoryName(item, shiftDetails, billingParticipants),
    [
      item?.invoice_category_name,
      shiftDetails?.invoice_category_name,
      shiftDetails?.participants,
      billingParticipants,
    ]
  );

  const advanceItemFetchTimes = useMemo(() => {
    const shiftDate = shiftDetails?.time_sheet_date;
    const breakdownStart =
      watchFormData[`start_time_${index}`] ??
      (item?.start_time ? extractTimeFromDateTime(item.start_time) : null);
    const breakdownEnd =
      watchFormData[`end_time_${index}`] ??
      (item?.end_time ? extractTimeFromDateTime(item.end_time) : null);

    if (isSplitBillingMode && breakdownStart && breakdownEnd && shiftDate) {
      return {
        start: embedDateWithTime(breakdownStart, shiftDate),
        end: embedDateWithTime(breakdownEnd, shiftDate),
      };
    }
    return { start: startTime, end: endTime };
  }, [
    isSplitBillingMode,
    watchFormData[`start_time_${index}`],
    watchFormData[`end_time_${index}`],
    index,
    item?.start_time,
    item?.end_time,
    shiftDetails?.time_sheet_date,
    startTime,
    endTime,
  ]);

  /** Query params for shift_ndis_items — refetch when any of these change. */
  const advanceNdisFetchParams = useMemo(() => {
    const serviceTypeId =
      watchFormData[`invoice_service_type_id_${index}`] ??
      item?.service_type_id ??
      item?.invoice_service_type_id;
    const participantId = billingParticipants?.[0]?.id ?? null;
    const shiftDate = formatShiftDateForNdisApi(shiftDetails?.time_sheet_date);
    const sleepover = Boolean(
      parseBooleanVal(watchFormData[`sleepover_${index}`]) ||
        parseBooleanVal(item?.sleepover)
    );
    const stateId = item?.state_id ?? shiftDetails?.state_id ?? null;
    const { start: segmentStart, end: segmentEnd } = advanceItemFetchTimes;

    return {
      serviceTypeId: serviceTypeId ?? null,
      participantId,
      shiftDate: shiftDate || null,
      sleepover,
      sta: Boolean(isStaShift),
      stateId,
      uuid: shiftDetails?.uuid ?? null,
      categoryName: advanceCategoryItemName ?? null,
      segmentStart: segmentStart ?? null,
      segmentEnd: segmentEnd ?? null,
    };
  }, [
    watchFormData[`invoice_service_type_id_${index}`],
    watchFormData[`sleepover_${index}`],
    watchFormData[`start_time_${index}`],
    watchFormData[`end_time_${index}`],
    index,
    item?.service_type_id,
    item?.invoice_service_type_id,
    item?.sleepover,
    item?.state_id,
    billingParticipants,
    shiftDetails?.time_sheet_date,
    shiftDetails?.uuid,
    shiftDetails?.state_id,
    advanceCategoryItemName,
    advanceItemFetchTimes.start,
    advanceItemFetchTimes.end,
    isStaShift,
  ]);

  const advanceNdisFetchParamsKey = useMemo(() => {
    const {
      serviceTypeId,
      participantId,
      shiftDate,
      sleepover,
      sta,
      stateId,
      uuid,
      categoryName,
      segmentStart,
      segmentEnd,
    } = advanceNdisFetchParams;
    const schedulingKey = {
      serviceTypeId,
      participantId,
      shiftDate,
      sleepover,
      sta,
    };
    if (participantId && shiftDate) {
      return `sched:${JSON.stringify(schedulingKey)}`;
    }
    return `ts:${JSON.stringify({
      ...schedulingKey,
      stateId,
      uuid,
      categoryName,
      segmentStart,
      segmentEnd,
    })}`;
  }, [advanceNdisFetchParams]);

  const effectiveItemNumbers = useMemo(() => {
    const baseOptions = itemNumber ?? [];
    // Split and advance breakdowns load the service-category catalogue into
    // advanceItemNumbers. A parent itemNumber list is only the saved row.
    let options =
      isAdvanceMode || legacySplitShiftOnly ? advanceItemNumbers : baseOptions;
    // Advance already remaps inside loadAdvanceNdisLineItems. Legacy basic split
    // must apply the same claim-type suffix so Travel/NF2F labels match.
    if (
      !isAdvanceMode &&
      legacySplitShiftOnly &&
      showClaimTypeFromBusinessSettings &&
      breakdownClaimType
    ) {
      options = mapNdisOptionsForClaimType(options, breakdownClaimType);
    }
    const formServiceId =
      watchFormData[`invoice_service_type_id_${index}`] ??
      item?.service_type_id ??
      item?.invoice_service_type_id;
    const itemServiceId =
      item?.service_type_id ?? item?.invoice_service_type_id;
    const serviceTypeChanged =
      (isAdvanceMode || legacySplitShiftOnly) &&
      formServiceId != null &&
      itemServiceId != null &&
      String(formServiceId) !== String(itemServiceId);
    if ((isAdvanceMode || legacySplitShiftOnly) && serviceTypeChanged) {
      return options;
    }
    return mergeBreakdownSavedLineItemOption(options, item);
  }, [
    isAdvanceMode,
    legacySplitShiftOnly,
    showClaimTypeFromBusinessSettings,
    breakdownClaimType,
    advanceItemNumbers,
    itemNumber,
    item?.invoice_item_id,
    item?.ndis_support_item_number,
    item?.support_item_number,
    item?.ndis_support_item_name,
    item?.support_item_name,
    item?.custom_line_item_uuid,
    item?.custom_line_item?.uuid,
    watchFormData[`invoice_service_type_id_${index}`],
    item?.service_type_id,
    item?.invoice_service_type_id,
    index,
  ]);

  const isSleepoverServiceCategory = isSleepoverServiceCategoryName(
    breakdownServiceCategory?.name
  );

  const breakdownHeaderSchedule = useMemo(() => {
    if (!isSplitBillingMode) return null;
    const { start, end } = resolveBreakdownScheduleTimes(item, billingSplitMeta);
    const shiftBounds =
      isV5 && startTime && endTime
        ? { shiftStartDateTime: startTime, shiftEndDateTime: endTime }
        : {};
    const formatted = formatBreakdownScheduleHeader(start, end, shiftBounds);
    if (!formatted.dateRange || !formatted.timeRange) return null;
    return formatted;
  }, [
    isSplitBillingMode,
    isV5,
    startTime,
    endTime,
    item?.start_time,
    item?.end_time,
    billingSplitMeta?.start_time,
    billingSplitMeta?.end_time,
  ]);

  const advanceParticipantNames = useMemo(
    () =>
      billingParticipants
        .map((p) =>
          capitalizeWords(
            `${p?.first_name || ""} ${p?.last_name || ""}`.trim()
          )
        )
        .filter(Boolean),
    [billingParticipants]
  );

  const advanceParticipantInitials = useMemo(() => {
    const p = billingParticipants[0];
    if (!p) return "?";
    const first = (p.first_name || "").charAt(0);
    const last = (p.last_name || "").charAt(0);
    const initials = `${first}${last}`.toUpperCase();
    return initials || "?";
  }, [billingParticipants]);

  const showAdvanceParticipantStrip =
    isAdvanceMode &&
    (advanceParticipantNames.length > 0 || billingSplitMeta?.ratio);

  const breakdownHeaderHasError =
    watchFormData[`start_time_${index}`] === undefined ||
    watchFormData[`start_time_${index}`] === null ||
    watchFormData[`start_time_${index}`] === "";

  const [activeSleepoverIndex, setActiveSleepoverIndex] = useState(null);
  useEffect(() => {
    if (!item) return;

    const setWhenChanged = (field, next) => {
      if (next === undefined) return;
      if (String(watchFormData?.[field] ?? "") === String(next ?? "")) return;
      setValue(field, next);
    };

    if (item.start_time) {
      setWhenChanged(
        `start_time_${index}`,
        extractTimeFromDateTime(item.start_time)
      );
    }
    if (item.end_time) {
      setWhenChanged(
        `end_time_${index}`,
        extractTimeFromDateTime(item.end_time)
      );
    }
    if (isAdvanceMode || legacySplitShiftOnly) {
      const serviceTypeId =
        item.service_type_id ?? item.invoice_service_type_id ?? null;
      if (serviceTypeId != null) {
        setWhenChanged(`invoice_service_type_id_${index}`, serviceTypeId);
      }
    }

    if (
      watchFormData?.[`ratio_${index}`] == null ||
      watchFormData?.[`ratio_${index}`] === ""
    ) {
      const ratioFromLineItem =
        extractRatioFromLineItemLabel(
          item?.support_item_name ||
            item?.ndis_support_item_name ||
            item?.ndis_support_item_number
        ) || null;
      const ratioValue =
        isAdvanceMode && isStaShift
          ? ratioFromLineItem || item?.ratio || "1:1"
          : resolveBreakdownDefaultRatio(
              shiftDetails,
              index,
              item,
              billingSplitMeta
            ) ??
            (billingParticipants.length > 1
              ? `1:${billingParticipants.length}`
              : shiftDetails?.participants?.length > 1
              ? `1:${shiftDetails.participants.length}`
              : "1:1");
      setWhenChanged(`ratio_${index}`, ratioValue);
    }

    const customLineItemUuid =
      item?.custom_line_item_uuid ?? item?.custom_line_item?.uuid ?? null;
    if (customLineItemUuid) {
      setWhenChanged(`invoice_item_id_${index}`, `custom:${customLineItemUuid}`);
      setWhenChanged(`custom_line_item_uuid_${index}`, customLineItemUuid);
    } else if (
      item?.invoice_item_id != null &&
      item?.invoice_item_id !== "" &&
      (isAdvanceMode || isSingleTimesheetBreakdowns || legacySplitShiftOnly)
    ) {
      setWhenChanged(`invoice_item_id_${index}`, item.invoice_item_id);
    }

    const nextSleepover = parseBooleanVal(
      item?.sleepover ?? item?.is_sleepover
    );
    setWhenChanged(`sleepover_${index}`, nextSleepover);

    // Basic / basic-split only. Advance hydrates Active Hours from its own path.
    if (!isAdvanceMode) {
      const child = Array.isArray(item?.children) ? item.children[0] : null;
      if (child) {
        const durationParts = String(child.duration || "").split(":");
        setWhenChanged(
          `active_hours_${index}`,
          durationParts[0]
            ? `${durationParts[0]}:${(durationParts[1] || "00").padStart(2, "0")}`
            : ""
        );
        if (child.invoice_item_id != null && child.invoice_item_id !== "") {
          setWhenChanged(
            `invoice_item_id_active_hours${index}`,
            child.invoice_item_id
          );
        }
        if (parseBooleanVal(child?.alternate_rate)) {
          setWhenChanged(`invoice_ndis_rate_active_hours${index}`, "alternate");
          if (child?.invoice_price != null && child?.invoice_price !== "") {
            setWhenChanged(
              `rate_amount_active_hours${index}`,
              child.invoice_price
            );
          }
        } else if (child?.invoice_price != null && child?.invoice_price !== "") {
          setWhenChanged(
            `invoice_ndis_rate_active_hours${index}`,
            child.invoice_price
          );
          setWhenChanged(
            `rate_amount_active_hours${index}`,
            child.invoice_price
          );
        } else if (child?.ndis_rate != null && child?.ndis_rate !== "") {
          setWhenChanged(
            `invoice_ndis_rate_active_hours${index}`,
            child.ndis_rate
          );
          setWhenChanged(`rate_amount_active_hours${index}`, child.ndis_rate);
        }
      }
    }

    if (!isNonBillable) {
      const hasLine =
        item?.invoice_item_id ||
        item?.custom_line_item_uuid ||
        item?.custom_line_item?.uuid;
      if (hasLine && parseBooleanVal(item?.alternate_rate)) {
        setWhenChanged(`invoice_ndis_rate_${index}`, "alternate");
        const ra = resolvePersistedBreakdownUnitRate(item);
        if (ra != null) {
          setWhenChanged(`rate_amount_${index}`, ra);
        }
      } else if (hasLine) {
        const rate = resolvePersistedBreakdownUnitRate(item);
        if (rate != null) {
          setWhenChanged(`invoice_ndis_rate_${index}`, rate);
          setWhenChanged(`rate_amount_${index}`, rate);
        }
      }
    }
    // Intentionally omit whole `item` / `billingSplitMeta` objects — they get new
    // references after Split breakdown and would re-fire setValue in a watch() loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- primitive field deps only
  }, [
    item?.start_time,
    item?.end_time,
    item?.invoice_item_id,
    item?.ndis_support_item_number,
    item?.ndis_support_item_name,
    item?.support_item_name,
    item?.custom_line_item_uuid,
    item?.custom_line_item?.uuid,
    item?.service_type_id,
    item?.invoice_service_type_id,
    item?.sleepover,
    item?.is_sleepover,
    item?.children?.[0]?.id,
    item?.children?.[0]?.invoice_item_id,
    item?.children?.[0]?.duration,
    item?.children?.[0]?.invoice_price,
    item?.children?.[0]?.ndis_rate,
    item?.children?.[0]?.alternate_rate,
    item?.alternate_rate,
    item?.ndis_rate,
    item?.invoice_price,
    item?.ratio,
    item?._clientKey,
    index,
    setValue,
    isNonBillable,
    isAdvanceMode,
    isStaShift,
    legacySplitShiftOnly,
    billingParticipants.length,
    shiftDetails?.participants?.length,
    shiftDetails?.ratio,
    isSingleTimesheetBreakdowns,
  ]);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedNDISItem, setSelectedNDISItem] = useState(null);
  const [selectedItemNumber, setSelectedItemNumber] = useState("");

  const getRateAmountForHourlyRate = useCallback(
    (rateValue) => {
      if (isNonBillable || rateValue == null || rateValue === "") return "";

      if (rateValue === "alternate") {
        if (parseBooleanVal(spSettings?.apply_alternate_rate)) {
          return spSettings?.default_alternate_rate || 0;
        }
        return item?.ndis_rate ?? 0;
      }

      const catalogueRate =
        selectedItemNumber?.price ?? item?.invoice_price ?? item?.ndis_rate;
      const selectedPrice = Number(catalogueRate);
      if (!isNaN(selectedPrice) && Number(rateValue) === selectedPrice) {
        return selectedPrice;
      }
      return Number(rateValue);
    },
    [
      isNonBillable,
      spSettings,
      item?.ndis_rate,
      item?.invoice_price,
      selectedItemNumber?.price,
    ]
  );

  const syncRateAmountFromHourlyRate = useCallback(
    (rateValue) => {
      const amount = getRateAmountForHourlyRate(rateValue);
      if (amount !== "") {
        setValue(`rate_amount_${index}`, amount);
      }
    },
    [getRateAmountForHourlyRate, index, setValue]
  );

  const getInvoiceItemOptionValue = (invItem) => {
    if (invItem?.is_custom && invItem?.custom_line_item_uuid) {
      return `custom:${invItem.custom_line_item_uuid}`;
    }
    return invItem?.id;
  };

  const mapInvoiceItemOption = (invItem) => ({
    label: `${invItem.support_item_name} - ${invItem?.support_item_number}`,
    value: getInvoiceItemOptionValue(invItem),
    price: invItem.price,
    number: invItem?.support_item_number,
    name: invItem?.support_item_name,
    ratio:
      extractRatioFromLineItemLabel(invItem?.support_item_name) || "1:1",
    isCustom: Boolean(invItem?.is_custom),
  });

  const advanceNdisFetchRef = React.useRef(0);
  const prevNdisFetchParamsKeyRef = React.useRef(null);
  const basicSplitActiveHoursFetchRef = React.useRef(0);
  const prevBasicSplitActiveHoursFetchParamsKeyRef = React.useRef(null);

  const isBasicSplitSleepoverAutoOn = Boolean(
    legacySplitShiftOnly &&
      !isAdvanceMode &&
      parseBooleanVal(watchFormData[`sleepover_${index}`]) &&
      String(watchFormData[`active_hours_${index}`] ?? "").trim()
  );

  // Active Hours is hourly/daytime work inside a sleepover window.
  // Parent segment keeps sleepover=true; this fetch must not.
  const basicSplitActiveHoursFetchParamsKey = useMemo(() => {
    const {
      serviceTypeId,
      participantId,
      shiftDate,
      sta,
      stateId,
      uuid,
      categoryName,
      segmentStart,
      segmentEnd,
    } = advanceNdisFetchParams;
    const schedulingKey = {
      serviceTypeId,
      participantId,
      shiftDate,
      sleepover: false,
      sta,
    };
    if (participantId && shiftDate) {
      return `basicSplitAh:sched:${JSON.stringify(schedulingKey)}`;
    }
    return `basicSplitAh:ts:${JSON.stringify({
      ...schedulingKey,
      stateId,
      uuid,
      categoryName,
      segmentStart,
      segmentEnd,
    })}`;
  }, [advanceNdisFetchParams]);

  const loadAdvanceNdisLineItems = useCallback(
    async ({ forceRefresh = false } = {}) => {
      if ((!isAdvanceMode && !legacySplitShiftOnly) || !shiftDetails?.uuid) {
        return;
      }

      const {
        serviceTypeId,
        participantId,
        shiftDate,
        sleepover,
        sta,
        stateId,
        uuid,
        categoryName,
        segmentStart,
        segmentEnd,
      } = advanceNdisFetchParams;

      if (!serviceTypeId) {
        return;
      }

      const canUseSchedulingApi = Boolean(participantId && shiftDate);
      const canUseTimesheetApi = Boolean(segmentStart && segmentEnd && stateId);

      if (!canUseSchedulingApi && !canUseTimesheetApi) {
        return;
      }

      const fetchId = ++advanceNdisFetchRef.current;
      setAdvanceItemLoading(true);

      if (forceRefresh) {
        if (canUseSchedulingApi) {
          invalidateShiftNdisLineItemsCache({
            serviceTypeId,
            participantId,
            shiftDate,
            sleepover,
            sta,
          });
        } else {
          invalidateTimesheetNdisLineItemsCache({
            serviceTypeId,
            stateId,
            uuid,
            sleepover,
            categoryName,
            startTime: segmentStart,
            endTime: segmentEnd,
          });
        }
      }

      try {
        const rawItems = canUseSchedulingApi
          ? await fetchShiftNdisLineItems({
              serviceTypeId,
              participantId,
              shiftDate,
              sleepover,
              sta,
            })
          : await fetchTimesheetNdisLineItems({
              serviceTypeId,
              stateId,
              uuid,
              sleepover,
              categoryName,
              startTime: segmentStart,
              endTime: segmentEnd,
            });

        if (fetchId !== advanceNdisFetchRef.current) {
          return;
        }

        let options = canUseSchedulingApi
          ? mapShiftNdisItemsToOptions(rawItems, { isSta: Boolean(sta) }).map(
              (opt) => ({
                label: opt.number ? `${opt.name} - ${opt.number}` : opt.label,
                value: opt.value,
                price: opt.price,
                number: opt.number,
                name: opt.name,
                ratio: opt.ratio || "1:1",
              })
            )
          : rawItems?.map(mapInvoiceItemOption) || [];
        if (showClaimTypeFromBusinessSettings && breakdownClaimType) {
          options = mapNdisOptionsForClaimType(options, breakdownClaimType);
        }
        const itemServiceId =
          item?.service_type_id ?? item?.invoice_service_type_id;
        const serviceTypeChanged =
          serviceTypeId != null &&
          itemServiceId != null &&
          String(serviceTypeId) !== String(itemServiceId);
        setAdvanceItemNumbers(
          serviceTypeChanged
            ? options
            : mergeBreakdownSavedLineItemOption(options, item)
        );
      } catch {
        if (fetchId === advanceNdisFetchRef.current) {
          setAdvanceItemNumbers([]);
        }
      } finally {
        if (fetchId === advanceNdisFetchRef.current) {
          setAdvanceItemLoading(false);
        }
      }
    },
    [
      isAdvanceMode,
      legacySplitShiftOnly,
      shiftDetails?.uuid,
      advanceNdisFetchParams,
      showClaimTypeFromBusinessSettings,
      breakdownClaimType,
      item,
    ]
  );

  useEffect(() => {
    const shouldLoadCatalogue = isAdvanceMode || legacySplitShiftOnly;
    if (!shouldLoadCatalogue || !shiftDetails?.uuid) {
      return undefined;
    }
    // Advance waits until the row is open. Split loads immediately so the
    // line-item menu is the service-category catalogue, not only the saved item.
    if (isAdvanceMode && !isOpen) {
      return undefined;
    }

    if (!advanceNdisFetchParams.serviceTypeId) {
      return undefined;
    }

    const forceRefresh =
      prevNdisFetchParamsKeyRef.current != null &&
      prevNdisFetchParamsKeyRef.current !== advanceNdisFetchParamsKey;

    const timeoutId = setTimeout(() => {
      prevNdisFetchParamsKeyRef.current = advanceNdisFetchParamsKey;
      loadAdvanceNdisLineItems({ forceRefresh });
    }, NDIS_ITEMS_FETCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    isAdvanceMode,
    legacySplitShiftOnly,
    isOpen,
    shiftDetails?.uuid,
    advanceNdisFetchParamsKey,
    advanceNdisFetchParams.serviceTypeId,
    loadAdvanceNdisLineItems,
  ]);

  const loadBasicSplitActiveHoursNdisItems = useCallback(
    async ({ forceRefresh = false } = {}) => {
      if (!legacySplitShiftOnly || isAdvanceMode || !shiftDetails?.uuid) {
        return;
      }
      if (!isBasicSplitSleepoverAutoOn) {
        return;
      }

      const {
        serviceTypeId,
        participantId,
        shiftDate,
        sta,
        stateId,
        uuid,
        categoryName,
        segmentStart,
        segmentEnd,
      } = advanceNdisFetchParams;

      if (!serviceTypeId) {
        return;
      }

      const canUseSchedulingApi = Boolean(participantId && shiftDate);
      const canUseTimesheetApi = Boolean(segmentStart && segmentEnd && stateId);

      if (!canUseSchedulingApi && !canUseTimesheetApi) {
        return;
      }

      const fetchId = ++basicSplitActiveHoursFetchRef.current;
      const sleepover = false;

      if (forceRefresh) {
        if (canUseSchedulingApi) {
          invalidateShiftNdisLineItemsCache({
            serviceTypeId,
            participantId,
            shiftDate,
            sleepover,
            sta,
          });
        } else {
          invalidateTimesheetNdisLineItemsCache({
            serviceTypeId,
            stateId,
            uuid,
            sleepover,
            categoryName,
            startTime: segmentStart,
            endTime: segmentEnd,
          });
        }
      }

      try {
        const rawItems = canUseSchedulingApi
          ? await fetchShiftNdisLineItems({
              serviceTypeId,
              participantId,
              shiftDate,
              sleepover,
              sta,
            })
          : await fetchTimesheetNdisLineItems({
              serviceTypeId,
              stateId,
              uuid,
              sleepover,
              categoryName,
              startTime: segmentStart,
              endTime: segmentEnd,
            });

        if (fetchId !== basicSplitActiveHoursFetchRef.current) {
          return;
        }

        let options = canUseSchedulingApi
          ? mapShiftNdisItemsToOptions(rawItems, { isSta: Boolean(sta) }).map(
              (opt) => ({
                label: opt.number ? `${opt.name} - ${opt.number}` : opt.label,
                value: opt.value,
                price: opt.price,
                number: opt.number,
                name: opt.name,
                ratio: opt.ratio || "1:1",
              })
            )
          : rawItems?.map(mapInvoiceItemOption) || [];
        if (showClaimTypeFromBusinessSettings && breakdownClaimType) {
          options = mapNdisOptionsForClaimType(options, breakdownClaimType);
        }
        const savedChild = item?.children?.[0];
        setBasicSplitActiveHoursItemNumbers(
          savedChild
            ? mergeBreakdownSavedLineItemOption(options, savedChild)
            : options
        );
      } catch {
        if (fetchId === basicSplitActiveHoursFetchRef.current) {
          setBasicSplitActiveHoursItemNumbers([]);
        }
      }
    },
    [
      legacySplitShiftOnly,
      isAdvanceMode,
      isBasicSplitSleepoverAutoOn,
      shiftDetails?.uuid,
      advanceNdisFetchParams,
      showClaimTypeFromBusinessSettings,
      breakdownClaimType,
      item,
    ]
  );

  useEffect(() => {
    if (!isBasicSplitSleepoverAutoOn) {
      setBasicSplitActiveHoursItemNumbers((prev) =>
        prev.length ? [] : prev
      );
      prevBasicSplitActiveHoursFetchParamsKeyRef.current = null;
      return undefined;
    }

    if (!isOpen || !shiftDetails?.uuid) {
      return undefined;
    }

    if (!advanceNdisFetchParams.serviceTypeId) {
      return undefined;
    }

    const forceRefresh =
      prevBasicSplitActiveHoursFetchParamsKeyRef.current != null &&
      prevBasicSplitActiveHoursFetchParamsKeyRef.current !==
        basicSplitActiveHoursFetchParamsKey;

    const timeoutId = setTimeout(() => {
      prevBasicSplitActiveHoursFetchParamsKeyRef.current =
        basicSplitActiveHoursFetchParamsKey;
      loadBasicSplitActiveHoursNdisItems({ forceRefresh });
    }, NDIS_ITEMS_FETCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    isBasicSplitSleepoverAutoOn,
    isOpen,
    shiftDetails?.uuid,
    basicSplitActiveHoursFetchParamsKey,
    advanceNdisFetchParams.serviceTypeId,
    loadBasicSplitActiveHoursNdisItems,
  ]);

  const val = watchFormData[`invoice_item_id_${index}`];
  const isStaLineItemSelected = Boolean(
    isStaShift && val != null && val !== ""
  );
  const isStaRatioLocked = Boolean(isAdvanceMode && isStaLineItemSelected);

  const resolveRatioFromLineItemOption = useCallback((option) => {
    return (
      option?.ratio ||
      extractRatioFromLineItemLabel(option?.name || option?.label) ||
      "1:1"
    );
  }, []);

  const selectedLineItemOption = useMemo(() => {
    const finalizeOption = (opt) => {
      if (!opt) return null;
      const number = resolveBreakdownSupportItemNumber(opt, item);
      if (!number || String(opt.number ?? "") === String(number)) return opt;
      return { ...opt, number };
    };

    const options = effectiveItemNumbers ?? [];
    if (!options.length) return null;

    if (selectedNDISItem?.value != null) {
      return finalizeOption(
        options.find((x) => String(x.value) === String(selectedNDISItem.value)) ??
          selectedNDISItem
      );
    }

    const customUuid =
      item?.custom_line_item_uuid ?? item?.custom_line_item?.uuid ?? null;
    if (customUuid) {
      const customValue = `custom:${customUuid}`;
      const customMatch = options.find(
        (x) => String(x.value) === customValue
      );
      if (customMatch) return finalizeOption(customMatch);
    }

    if (isAdvanceMode || legacySplitShiftOnly) {
      const formServiceId =
        watchFormData[`invoice_service_type_id_${index}`] ??
        item?.service_type_id ??
        item?.invoice_service_type_id;
      const itemServiceId =
        item?.service_type_id ?? item?.invoice_service_type_id;
      const serviceTypeChanged =
        formServiceId != null &&
        itemServiceId != null &&
        String(formServiceId) !== String(itemServiceId);

      if (!serviceTypeChanged) {
        const fromSaved = buildNdisSelectedOption(
          toBreakdownNdisSelectionShape(item),
          options
        );
        if (fromSaved) {
          return finalizeOption(
            options.find(
              (x) =>
                String(x.value) === String(fromSaved.value) ||
                (fromSaved.number &&
                  String(x.number) === String(fromSaved.number))
            ) ??
              mergeBreakdownSavedLineItemOption([], item)[0] ??
              fromSaved
          );
        }
      }
    }

    const lookupValue =
      customUuid != null
        ? `custom:${customUuid}`
        : val ?? item?.invoice_item_id ?? null;

    if (lookupValue != null && lookupValue !== "") {
      const match = options.find((x) => String(x.value) === String(lookupValue));
      if (match) return finalizeOption(match);
    }

    if (suggestedItemNumber) {
      const suggestedValue =
        suggestedItemNumber?.is_custom && suggestedItemNumber?.custom_line_item_uuid
          ? `custom:${suggestedItemNumber.custom_line_item_uuid}`
          : suggestedItemNumber?.id ?? suggestedItemNumber?.value;
      if (suggestedValue != null) {
        const byId = options.find(
          (x) => String(x.value) === String(suggestedValue)
        );
        if (byId) return finalizeOption(byId);
        if (suggestedItemNumber?.support_item_number) {
          return finalizeOption(
            options.find(
              (x) =>
                String(x.number) ===
                String(suggestedItemNumber.support_item_number)
            ) ?? null
          );
        }
      }
    }

    return null;
  }, [
    effectiveItemNumbers,
    selectedNDISItem?.value,
    selectedNDISItem?.number,
    selectedNDISItem?.price,
    isAdvanceMode,
    legacySplitShiftOnly,
    val,
    suggestedItemNumber?.id,
    suggestedItemNumber?.value,
    suggestedItemNumber?.support_item_number,
    suggestedItemNumber?.is_custom,
    suggestedItemNumber?.custom_line_item_uuid,
    watchFormData[`invoice_service_type_id_${index}`],
    item?.service_type_id,
    item?.invoice_service_type_id,
    item?.invoice_item_id,
    item?.custom_line_item_uuid,
    item?.custom_line_item?.uuid,
    item?.ndis_support_item_number,
    item?.support_item_number,
    item?.ndis_support_item_name,
    item?.support_item_name,
  ]);

  const advanceServiceCategoryChanged = useMemo(() => {
    if (!isAdvanceMode) return false;
    const formServiceId = watchFormData[`invoice_service_type_id_${index}`];
    const itemServiceId =
      item?.service_type_id ?? item?.invoice_service_type_id;
    return (
      formServiceId != null &&
      itemServiceId != null &&
      String(formServiceId) !== String(itemServiceId)
    );
  }, [
    isAdvanceMode,
    watchFormData[`invoice_service_type_id_${index}`],
    item?.service_type_id,
    item?.invoice_service_type_id,
    index,
  ]);

  const cataloguePrice = useMemo(() => {
    if (
      selectedItemNumber &&
      typeof selectedItemNumber === "object" &&
      selectedItemNumber.price != null
    ) {
      return selectedItemNumber.price;
    }
    const lineItemId = val ?? item?.invoice_item_id;
    const fromOptions = effectiveItemNumbers?.find(
      (x) => String(x.value) === String(lineItemId)
    )?.price;
    if (fromOptions != null) return fromOptions;
    const fromItemList = item?.item_numbers?.items?.find(
      (el) => String(el.id) === String(lineItemId)
    )?.price;
    if (fromItemList != null) return fromItemList;
    if (advanceServiceCategoryChanged) {
      return null;
    }
    if (item?.invoice_price != null && item?.invoice_price !== "") {
      return item.invoice_price;
    }
    if (item?.ndis_rate != null && item?.ndis_rate !== "") {
      return item.ndis_rate;
    }
    return null;
  }, [
    selectedItemNumber,
    val,
    item?.invoice_item_id,
    item?.invoice_price,
    item?.ndis_rate,
    itemNumber,
    effectiveItemNumbers,
    item?.item_numbers?.items,
    advanceServiceCategoryChanged,
  ]);

  const hourlyRateCataloguePrice = useMemo(() => {
    if (cataloguePrice != null && cataloguePrice !== "") {
      return cataloguePrice;
    }
    if (
      selectedLineItemOption?.price != null &&
      selectedLineItemOption.price !== ""
    ) {
      return selectedLineItemOption.price;
    }
    return null;
  }, [cataloguePrice, selectedLineItemOption?.price]);

  const NDIS_RATE = useMemo(
    () => [
      {
        label: "Apply Alternative Rate",
        value: "alternate",
      },
      ...(hourlyRateCataloguePrice != null && hourlyRateCataloguePrice !== ""
        ? [
            {
              label: String(hourlyRateCataloguePrice),
              value: hourlyRateCataloguePrice,
            },
          ]
        : []),
    ],
    [hourlyRateCataloguePrice]
  );

  const findNdisRateOption = useCallback(
    (rateValue) => {
      if (rateValue == null || rateValue === "") return null;
      if (String(rateValue) === "alternate") {
        return NDIS_RATE[0] ?? null;
      }
      return (
        NDIS_RATE.find((x) => String(x.value) === String(rateValue)) ?? null
      );
    },
    [NDIS_RATE]
  );

  const usesAlternateRate = useMemo(
    () => breakdownItemUsesAlternateRate(item, cataloguePrice),
    [
      item?.alternate_rate,
      item?.ndis_rate,
      item?.invoice_price,
      cataloguePrice,
    ]
  );

  const resolvedInvoiceNdisRate = useMemo(() => {
    const stored = watchFormData?.[`invoice_ndis_rate_${index}`];
    if (stored !== undefined && stored !== null && stored !== "") {
      return stored;
    }
    if (advanceServiceCategoryChanged) {
      return "";
    }
    if (usesAlternateRate) {
      return "alternate";
    }
    if (item?.invoice_price != null && item?.invoice_price !== "") {
      return item.invoice_price;
    }
    if (item?.ndis_rate != null && item?.ndis_rate !== "") {
      return item.ndis_rate;
    }
    return "";
  }, [
    watchFormData?.[`invoice_ndis_rate_${index}`],
    usesAlternateRate,
    item?.invoice_price,
    item?.ndis_rate,
    index,
    advanceServiceCategoryChanged,
  ]);

  const getHourlyRateSelectValue = useCallback(
    (fieldValue) => {
      if (fieldValue === "alternate") {
        return NDIS_RATE[0] ?? null;
      }
      if (fieldValue != null && fieldValue !== "") {
        return (
          NDIS_RATE.find((x) => String(x.value) === String(fieldValue)) ?? null
        );
      }
      if (usesAlternateRate) {
        return NDIS_RATE[0] ?? null;
      }
      if (advanceServiceCategoryChanged) {
        return null;
      }
      const catalogueVal = cataloguePrice ?? item?.invoice_price ?? item?.ndis_rate;
      if (catalogueVal != null && catalogueVal !== "") {
        return (
          NDIS_RATE.find((x) => String(x.value) === String(catalogueVal)) ??
          NDIS_RATE[1] ??
          null
        );
      }
      return null;
    },
    [NDIS_RATE, usesAlternateRate, cataloguePrice, item?.invoice_price, item?.ndis_rate, advanceServiceCategoryChanged]
  );

  const resolvedRateAmount = useMemo(() => {
    const stored = watchFormData?.[`rate_amount_${index}`];
    const isAlternateMode =
      watchFormData?.[`invoice_ndis_rate_${index}`] === "alternate" ||
      resolvedInvoiceNdisRate === "alternate";

    /* Catalogue: ignore empty string in form so first load can resolve from item/catalogue */
    if (stored !== undefined && stored !== null) {
      if (isAlternateMode || stored !== "") {
        return stored;
      }
    }

    if (advanceServiceCategoryChanged) {
      return "";
    }

    const hourlyRate = resolvedInvoiceNdisRate;
    if (hourlyRate && hourlyRate !== "alternate") {
      const derived = getRateAmountForHourlyRate(hourlyRate);
      if (derived !== "") return derived;
    }
    if (hourlyRate === "alternate") {
      const derived = getRateAmountForHourlyRate("alternate");
      if (derived !== "") return derived;
    }
    const catalogueVal = cataloguePrice ?? item?.invoice_price ?? item?.ndis_rate;
    if (catalogueVal != null && catalogueVal !== "") {
      return catalogueVal;
    }
    return "";
  }, [
    watchFormData?.[`rate_amount_${index}`],
    resolvedInvoiceNdisRate,
    getRateAmountForHourlyRate,
    cataloguePrice,
    item?.ndis_rate,
    item?.invoice_price,
    index,
    advanceServiceCategoryChanged,
  ]);

  const hydratedBreakdownKeyRef = React.useRef(null);
  const spAlternateDefaultAppliedRef = React.useRef(false);
  const isInitialRender = React.useRef(true);
  const prevInvoiceItemIdForRateRef = React.useRef(null);
  const prevIsNonBillableRef = React.useRef(isNonBillable);

  useEffect(() => {
    if (isNonBillable) {
      setValue(`rate_amount_${index}`, 0);
      setValue(`applied_rate_${index}`, 0);
      setValue(`invoice_ndis_rate_${index}`, NDIS_RATE[0].value);
      prevIsNonBillableRef.current = true;
      return;
    }
    // Only a non-billable → billable flip re-arms hydration. NDIS_RATE changes
    // identity on every price change, which re-hydrated the saved rate over a
    // freshly picked line item.
    if (prevIsNonBillableRef.current) {
      hydratedBreakdownKeyRef.current = null;
      isInitialRender.current = true;
    }
    prevIsNonBillableRef.current = false;
  }, [isNonBillable, index, setValue, isOpen, NDIS_RATE]);

  useEffect(() => {
    if (item && val) {
      const options = effectiveItemNumbers;
      const selectedItem = options?.find(
        (opt) => String(opt.value) === String(val)
      );

      if (selectedItem) {
        setSelectedItemNumber((prev) =>
          prev?.value === selectedItem.value ? prev : selectedItem
        );
        if (!isAdvanceMode) {
          setSelectedNDISItem((prev) =>
            String(prev?.value) === String(selectedItem.value)
              ? prev
              : selectedItem
          );
        }
      }
    }
  }, [isAdvanceMode, effectiveItemNumbers, val, item?.invoice_item_id, item?.id]);

  const prevBasicCatalogSuggestedKeyRef = React.useRef(null);

  useEffect(() => {
    prevBasicCatalogSuggestedKeyRef.current = null;
  }, [shiftDetails?.uuid]);

  /* Basic mode: apply API-suggested NDIS line when empty, catalog changes, or stale selection */
  useEffect(() => {
    if (isAdvanceMode || isNonBillable) return;

    const suggestedId =
      suggestedItemNumber?.id ??
      suggestedItemNumber?.value ??
      item?.item_numbers?.selected?.id;
    if (suggestedId == null || suggestedId === "") return;

    const options = effectiveItemNumbers ?? [];
    if (!options.length) return;

    const match = options.find((o) => String(o.value) === String(suggestedId));
    if (!match) return;

    const catalogSuggestedKey = `${serviceCategory?.value ?? ""}|${String(suggestedId)}`;
    const prevCatalogSuggestedKey = prevBasicCatalogSuggestedKeyRef.current;
    const catalogChanged =
      prevCatalogSuggestedKey != null &&
      prevCatalogSuggestedKey !== catalogSuggestedKey;
    // First paint fills the category in async ("" → "14"); that is not a user switch.
    const catalogSwitchedByUser =
      catalogChanged && prevCatalogSuggestedKey.split("|")[0] !== "";

    const currentVal = watchFormData[`invoice_item_id_${index}`];
    const currentMatchesSuggested =
      currentVal != null &&
      currentVal !== "" &&
      String(currentVal) === String(match.value);

    if (currentMatchesSuggested && !catalogChanged) {
      prevBasicCatalogSuggestedKeyRef.current = catalogSuggestedKey;
      return;
    }

    const currentInOptions =
      currentVal != null &&
      currentVal !== "" &&
      options.some((o) => String(o.value) === String(currentVal));

    const shouldApply =
      currentVal == null ||
      currentVal === "" ||
      catalogChanged ||
      !currentInOptions;

    if (!shouldApply) return;
    if (String(currentVal) === String(match.value)) {
      prevBasicCatalogSuggestedKeyRef.current = catalogSuggestedKey;
      return;
    }

    setValue(`invoice_item_id_${index}`, match.value);
    setSelectedNDISItem(match);
    setSelectedItemNumber(match);
    // Rate from the old catalogue no longer exists in the new one — follow the new item.
    if (catalogSwitchedByUser && match.price != null && match.price !== "") {
      setValue(`invoice_ndis_rate_${index}`, match.price);
      setValue(`rate_amount_${index}`, match.price);
    }
    prevBasicCatalogSuggestedKeyRef.current = catalogSuggestedKey;
  }, [
    isAdvanceMode,
    isNonBillable,
    suggestedItemNumber?.id,
    suggestedItemNumber?.value,
    item?.item_numbers?.selected?.id,
    effectiveItemNumbers,
    serviceCategory?.value,
    index,
    setValue,
    watchFormData[`invoice_item_id_${index}`],
    shiftDetails?.uuid,
  ]);

  useEffect(() => {
    if (!selectedLineItemOption?.value) return;
    setSelectedItemNumber((prev) => {
      if (
        prev &&
        typeof prev === "object" &&
        String(prev.value) === String(selectedLineItemOption.value) &&
        String(prev.number ?? "") ===
          String(selectedLineItemOption.number ?? "") &&
        String(prev.price ?? "") === String(selectedLineItemOption.price ?? "")
      ) {
        return prev;
      }
      return selectedLineItemOption;
    });
    // Primitives only — full option object is recreated after Split and would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLineItemOption?.value,
    selectedLineItemOption?.number,
    selectedLineItemOption?.price,
  ]);

  /* STA advance: keep ratio locked to the selected NDIS line item (default 1:1). */
  useEffect(() => {
    if (!isAdvanceMode || !isStaShift || !isStaLineItemSelected) return;

    const ratio = resolveRatioFromLineItemOption(
      selectedLineItemOption ?? {
        name:
          item?.support_item_name ||
          item?.ndis_support_item_name ||
          item?.ndis_support_item_number,
        label: item?.support_item_name,
        ratio: item?.ratio,
      }
    );

    if (watchFormData?.[`ratio_${index}`] === ratio) return;
    setValue(`ratio_${index}`, ratio);
  }, [
    isAdvanceMode,
    isStaShift,
    isStaLineItemSelected,
    selectedLineItemOption,
    item?.support_item_name,
    item?.ndis_support_item_name,
    item?.ndis_support_item_number,
    item?.ratio,
    watchFormData?.[`ratio_${index}`],
    index,
    setValue,
    resolveRatioFromLineItemOption,
  ]);

  const getBreakdownHydrationKey = (breakdownItem) => {
    if (breakdownItem?._clientKey) return `client:${breakdownItem._clientKey}`;
    if (breakdownItem?.id != null) return `id:${breakdownItem.id}`;
    if (breakdownItem?.uuid) return `uuid:${breakdownItem.uuid}`;
    if (breakdownItem?.start_time && breakdownItem?.end_time) {
      return `seg:${breakdownItem.start_time}|${breakdownItem.end_time}|${
        breakdownItem.shift_type ?? ""
      }|${index}`;
    }
    return `idx:${index}`;
  };

  /* Re-hydrate from server only when this breakdown row identity changes (e.g. after save) */
  useEffect(() => {
    if (!item || isNonBillable) return;

    const hydrationKey = getBreakdownHydrationKey(item);
    if (!hydrationKey || hydratedBreakdownKeyRef.current === hydrationKey) {
      return;
    }
    hydratedBreakdownKeyRef.current = hydrationKey;
    spAlternateDefaultAppliedRef.current = false;

    const ratioFromLineItem =
      extractRatioFromLineItemLabel(
        item?.support_item_name ||
          item?.ndis_support_item_name ||
          item?.ndis_support_item_number
      ) || null;
    const ratioValue =
      isAdvanceMode && isStaShift
        ? ratioFromLineItem || item?.ratio || "1:1"
        : resolveBreakdownDefaultRatio(
            shiftDetails,
            index,
            item,
            billingSplitMeta
          ) ??
          (billingParticipants.length > 1
            ? `1:${billingParticipants.length}`
            : shiftDetails?.participants?.length > 1
            ? `1:${shiftDetails.participants.length}`
            : "1:1");
    setValue(`ratio_${index}`, ratioValue);

    const catalogueVal =
      cataloguePrice ??
      item?.item_numbers?.selected?.price ??
      item?.invoice_price ??
      item?.ndis_rate;

    const unitRate = resolvePersistedBreakdownUnitRate({
      ...item,
      item_numbers: {
        ...(item?.item_numbers || {}),
        selected:
          item?.item_numbers?.selected &&
          typeof item.item_numbers.selected === "object"
            ? item.item_numbers.selected
            : catalogueVal != null && catalogueVal !== ""
              ? { price: catalogueVal }
              : item?.item_numbers?.selected,
      },
      catalogue_price: catalogueVal,
    });

    if (breakdownItemUsesAlternateRate(item, cataloguePrice)) {
      setValue(`invoice_ndis_rate_${index}`, "alternate");
      if (unitRate != null) {
        setValue(`rate_amount_${index}`, unitRate);
      }
    } else if (unitRate != null) {
      setValue(`invoice_ndis_rate_${index}`, unitRate);
      setValue(`rate_amount_${index}`, unitRate);
    } else if (catalogueVal != null && catalogueVal !== "") {
      setValue(`invoice_ndis_rate_${index}`, catalogueVal);
      setValue(`rate_amount_${index}`, catalogueVal);
    }
  }, [
    item?.id,
    item?.uuid,
    item?.alternate_rate,
    item?.ndis_rate,
    item?.invoice_price,
    item?.item_numbers?.selected?.price,
    item?.support_item_name,
    item?.ndis_support_item_name,
    item?.ndis_support_item_number,
    item?.ratio,
    index,
    setValue,
    isNonBillable,
    isAdvanceMode,
    isStaShift,
    cataloguePrice,
    billingSplitMeta,
    billingParticipants.length,
    shiftDetails?.participants?.length,
    shiftDetails,
  ]);

  const resolvedSupportItemNumber = useMemo(
    () =>
      resolveBreakdownSupportItemNumber(
        selectedItemNumber && typeof selectedItemNumber === "object"
          ? selectedItemNumber
          : selectedLineItemOption,
        item
      ),
    [
      selectedItemNumber?.value,
      selectedItemNumber?.number,
      selectedLineItemOption?.value,
      selectedLineItemOption?.number,
      selectedLineItemOption?.label,
      item?.ndis_support_item_number,
      item?.support_item_number,
      item?.ndis_support_item_name,
      item?.support_item_name,
      item?.item_numbers?.selected?.support_item_number,
    ]
  );

  // Support item number is display-only (resolvedSupportItemNumber); do not setValue
  // into RHF on every resolve — that re-triggers parent watch() and blinks selects.

  // useEffect(() => {
  //   if (isSingleTimesheetBreakdowns) {
  //     const selectedPrice = Number(selectedItemNumber?.price);
  //     const suggestedPrice = Number(suggestedItemNumber?.price);

  //     if (!isNaN(selectedPrice) && !isNaN(suggestedPrice)) {
  //       if (selectedPrice !== suggestedPrice) {
  //         setValue(`invoice_ndis_rate_${index}`, "alternate");
  //         setValue(`rate_amount_${index}`, suggestedPrice);
  //       } else {
  //         setValue(`invoice_ndis_rate_${index}`, selectedItemNumber?.price);
  //         setValue(`rate_amount_${index}`, selectedPrice);
  //       }
  //     }
  //   }
  // }, [
  //   isSingleTimesheetBreakdowns,
  //   selectedItemNumber?.price,
  //   suggestedItemNumber?.price,
  //   index,
  //   setValue,
  // ]);
useEffect(() => {
  if (!isSingleTimesheetBreakdowns || isNonBillable) return;

  const suggestedPrice = Number(suggestedItemNumber?.price);
  const currentItemId =
    val ??
    (selectedItemNumber && typeof selectedItemNumber === "object"
      ? selectedItemNumber.value
      : null) ??
    item?.invoice_item_id;

  const effectiveCataloguePrice =
    (selectedItemNumber &&
      typeof selectedItemNumber === "object" &&
      selectedItemNumber.price != null &&
      selectedItemNumber.price !== "") ||
    cataloguePrice != null
      ? selectedItemNumber?.price ?? cataloguePrice
      : item?.invoice_price ?? item?.ndis_rate;

  if (
    effectiveCataloguePrice == null ||
    effectiveCataloguePrice === "" ||
    isNaN(Number(effectiveCataloguePrice))
  ) {
    return;
  }

  const selectedPrice = Number(effectiveCataloguePrice);

  const itemChanged =
    prevInvoiceItemIdForRateRef.current != null &&
    currentItemId != null &&
    prevInvoiceItemIdForRateRef.current !== currentItemId;

  if (isInitialRender.current) {
    if (breakdownItemUsesAlternateRate(item, cataloguePrice)) {
      setValue(`invoice_ndis_rate_${index}`, "alternate");
      const amount =
        item?.ndis_rate != null && item?.ndis_rate !== ""
          ? item.ndis_rate
          : !isNaN(suggestedPrice)
          ? suggestedPrice
          : selectedPrice;
      if (amount !== "" && amount != null) {
        setValue(`rate_amount_${index}`, amount);
      }
    } else if (!isNaN(suggestedPrice) && selectedPrice !== suggestedPrice) {
      // Catalog id/version remap can make suggested ≠ current option price.
      // Only treat as alternate when the billing line is actually alternate.
      if (parseBooleanVal(item?.alternate_rate)) {
        setValue(`invoice_ndis_rate_${index}`, "alternate");
        setValue(`rate_amount_${index}`, suggestedPrice);
      } else {
        setValue(`invoice_ndis_rate_${index}`, suggestedPrice);
        setValue(`rate_amount_${index}`, suggestedPrice);
      }
    } else {
      setValue(`invoice_ndis_rate_${index}`, effectiveCataloguePrice);
      setValue(`rate_amount_${index}`, selectedPrice);
    }
    isInitialRender.current = false;
    prevInvoiceItemIdForRateRef.current = currentItemId;
  } else if (itemChanged) {
    setValue(`invoice_ndis_rate_${index}`, effectiveCataloguePrice);
    setValue(`rate_amount_${index}`, selectedPrice);
    prevInvoiceItemIdForRateRef.current = currentItemId;
  }
}, [
  isSingleTimesheetBreakdowns,
  selectedItemNumber?.price,
  selectedItemNumber?.value,
  cataloguePrice,
  suggestedItemNumber?.price,
  item?.alternate_rate,
  item?.ndis_rate,
  item?.invoice_price,
  item?.invoice_item_id,
  index,
  setValue,
  isNonBillable,
  val,
]);
  // useEffect(() => {
  //   if (isOpen 
  //     && 
  //     (rateAmount === undefined || rateAmount === null)
  //   ) {
  //     let rate = null;
  //     if (isSingleTimesheetBreakdowns) {
  //       const selectedPrice = Number(selectedItemNumber?.price);
  //       const suggestedPrice = Number(suggestedItemNumber?.price);
  //       if (!isNaN(selectedPrice) && !isNaN(suggestedPrice)) {
  //         rate =
  //           selectedPrice !== suggestedPrice ? suggestedPrice : selectedPrice;
  //       }
  //     } else if (invoiceNdisRate === "alternate") {
  //       rate = parseBooleanVal(spSettings?.apply_alternate_rate)
  //         ? spSettings?.default_alternate_rate || 0
  //         : item?.ndis_rate;
  //     } else {
  //       rate = invoiceNdisRate || selectedItemNumber?.price || 0;
  //     }
  //     console.log("Setting rate:", rate);
  //     if (rate !== null && rate !== undefined) {
  //       setValue(`rate_amount_${index}`, rate);
  //     }
  //   }
  // }, [
  //   isOpen,
  //   index,
  //   isSingleTimesheetBreakdowns,
  //   selectedItemNumber,
  //   suggestedItemNumber,
  //   spSettings,
  //   item,
  //   setValue,
  //   rateAmount,
  //   invoiceNdisRate,
  // ]);
  /* Code to handle the case when the itemNumber is not selected */
  useEffect(() => {
    const storedRate = watchFormData[`invoice_ndis_rate_${index}`];
    const cataloguePriceForItem = selectedItemNumber?.price;
    // 0 is a valid catalogue rate: only an empty field counts as "not selected".
    if (
      (storedRate == null || storedRate === "") &&
      cataloguePriceForItem != null &&
      cataloguePriceForItem !== "" &&
      !isSingleTimesheetBreakdowns &&
      !isNonBillable &&
      !advanceServiceCategoryChanged
    ) {
      setValue(`invoice_ndis_rate_${index}`, cataloguePriceForItem);
    }
  }, [
    selectedItemNumber?.price,
    index,
    setValue,
    watchFormData[`invoice_ndis_rate_${index}`],
    isSingleTimesheetBreakdowns,
    isNonBillable,
    advanceServiceCategoryChanged,
  ]);
  /* Code to handle the case when the itemNumber is not selected */

  /* Hydration runs before the catalogue options resolve, so it seeds the hourly
     rate from item.invoice_price. On a $0 line item that seed survives as a rate
     the loaded catalogue no longer offers, so the dropdown renders blank while
     Rate Amount still shows the stale figure. Realign once the real catalogue
     price is known — this only fires while no option matches the stored rate. */
  useEffect(() => {
    if (isNonBillable || advanceServiceCategoryChanged) return;
    if (
      hourlyRateCataloguePrice == null ||
      hourlyRateCataloguePrice === ""
    ) {
      return;
    }

    const current = watchFormData[`invoice_ndis_rate_${index}`];
    if (current == null || current === "" || current === "alternate") return;
    // An explicit alternate line keeps its own rate.
    if (parseBooleanVal(item?.alternate_rate)) return;
    if (String(current) === String(hourlyRateCataloguePrice)) return;

    setValue(`invoice_ndis_rate_${index}`, hourlyRateCataloguePrice);
    setValue(`rate_amount_${index}`, hourlyRateCataloguePrice);
  }, [
    hourlyRateCataloguePrice,
    watchFormData[`invoice_ndis_rate_${index}`],
    item?.alternate_rate,
    index,
    setValue,
    isNonBillable,
    advanceServiceCategoryChanged,
  ]);

  const prevHourlyRateSelectionRef = React.useRef(undefined);

  useEffect(() => {
    if (advanceServiceCategoryChanged) {
      prevHourlyRateSelectionRef.current = "";
    }
  }, [advanceServiceCategoryChanged]);

  /* Sync rate_amount only when hourly rate selection changes, not while user edits amount */
  useEffect(() => {
    if (isNonBillable) return;

    const current = watchFormData[`invoice_ndis_rate_${index}`];
    if (current === prevHourlyRateSelectionRef.current) return;

    const previous = prevHourlyRateSelectionRef.current;
    prevHourlyRateSelectionRef.current = current;

    // A $0 catalogue rate is a real selection — only skip a genuinely empty field.
    if (current == null || current === "") return;

    if (current === "alternate" && previous !== "alternate") {
      // New alternate pick starts at 0. A persisted alternate (reload/save)
      // must keep its saved amount — do not wipe 120 → 0.
      if (legacySplitShiftOnly) {
        const persisted = parseBooleanVal(item?.alternate_rate)
          ? resolvePersistedBreakdownUnitRate(item)
          : null;
        if (persisted != null) {
          setValue(`rate_amount_${index}`, persisted);
        } else {
          const existing = watchFormData[`rate_amount_${index}`];
          if (existing === undefined || existing === null || existing === "") {
            setValue(`rate_amount_${index}`, 0);
          }
        }
      } else {
        syncRateAmountFromHourlyRate("alternate");
      }
    } else if (current !== "alternate") {
      const selectedPrice = Number(selectedItemNumber?.price);
      const nextAmount =
        !isNaN(selectedPrice) && Number(current) === selectedPrice
          ? selectedPrice
          : Number(current);
      // Number("") === 0 — do not wipe catalogue rows when hourly rate is briefly empty.
      if (Number.isNaN(nextAmount) || (nextAmount === 0 && current !== 0 && current !== "0")) {
        return;
      }
      const stored = watchFormData[`rate_amount_${index}`];
      if (String(stored) !== String(nextAmount)) {
        setValue(`rate_amount_${index}`, nextAmount);
      }
    }
  }, [
    watchFormData[`invoice_ndis_rate_${index}`],
    index,
    setValue,
    isNonBillable,
    selectedItemNumber?.price,
    syncRateAmountFromHourlyRate,
    legacySplitShiftOnly,
  ]);

  /* Keep catalogue rate_amount in form sync with displayed value (read-only field) */
  useEffect(() => {
    if (isNonBillable || resolvedInvoiceNdisRate === "alternate") return;

    const stored = watchFormData?.[`rate_amount_${index}`];
    const displayAmount = resolvedRateAmount;
    if (displayAmount === "" || displayAmount == null) return;

    if (
      (stored === "" || stored === undefined || stored === null) &&
      String(stored) !== String(displayAmount)
    ) {
      setValue(`rate_amount_${index}`, displayAmount);
    }
  }, [
    resolvedRateAmount,
    resolvedInvoiceNdisRate,
    index,
    setValue,
    isNonBillable,
    watchFormData?.[`rate_amount_${index}`],
  ]);

  /* Initial fill when rate_amount not in form yet (undefined/null), not when user cleared to "" */
  useEffect(() => {
    if (isNonBillable || !item) return;

    if (advanceServiceCategoryChanged) return;

    const stored = watchFormData?.[`rate_amount_${index}`];
    const hourlyRate = watchFormData?.[`invoice_ndis_rate_${index}`];

    /* Respect existing hourly rate — only backfill when amount was never set.
       Do NOT treat "" as missing: user may be clearing digits to type a new value. */
    if (
      hourlyRate !== undefined &&
      hourlyRate !== null &&
      hourlyRate !== ""
    ) {
      if (stored === undefined || stored === null) {
        if (legacySplitShiftOnly && hourlyRate === "alternate") {
          const persisted = resolvePersistedBreakdownUnitRate(item);
          setValue(`rate_amount_${index}`, persisted != null ? persisted : 0);
        } else {
          syncRateAmountFromHourlyRate(hourlyRate);
        }
      }
      return;
    }

    if (stored !== undefined && stored !== null && stored !== "") return;

    if (breakdownItemUsesAlternateRate(item, cataloguePrice)) {
      setValue(`invoice_ndis_rate_${index}`, "alternate");
      const savedRateAmount =
        item?.ndis_rate != null && item?.ndis_rate !== ""
          ? item.ndis_rate
          : item?.invoice_price;
      if (savedRateAmount != null && savedRateAmount !== "") {
        setValue(`rate_amount_${index}`, savedRateAmount);
      }
      return;
    }

    const catalogueVal =
      cataloguePrice ?? item?.invoice_price ?? item?.ndis_rate;
    if (catalogueVal != null && catalogueVal !== "") {
      setValue(`invoice_ndis_rate_${index}`, catalogueVal);
      setValue(`rate_amount_${index}`, catalogueVal);
    }
  }, [
    index,
    isNonBillable,
    item?.id,
    item?.uuid,
    item?.ndis_rate,
    item?.invoice_price,
    item?.alternate_rate,
    cataloguePrice,
    watchFormData?.[`invoice_ndis_rate_${index}`],
    watchFormData?.[`rate_amount_${index}`],
    syncRateAmountFromHourlyRate,
    setValue,
    advanceServiceCategoryChanged,
    legacySplitShiftOnly,
  ]);

  useEffect(() => {
    if (!parseBooleanVal(spSettings?.apply_alternate_rate)) return;
    if (advanceServiceCategoryChanged) return;
    if (breakdownItemUsesAlternateRate(item, cataloguePrice)) return;

    const currentRate = watchFormData?.[`invoice_ndis_rate_${index}`];
    if (
      currentRate !== undefined &&
      currentRate !== null &&
      currentRate !== ""
    ) {
      return;
    }
    if (spAlternateDefaultAppliedRef.current) return;

    spAlternateDefaultAppliedRef.current = true;
    setValue(`invoice_ndis_rate_${index}`, "alternate");
    setValue(`rate_amount_${index}`, spSettings?.default_alternate_rate || 0);
  }, [
    spSettings?.apply_alternate_rate,
    spSettings?.default_alternate_rate,
    item?.alternate_rate,
    item?.id,
    index,
    setValue,
    cataloguePrice,
    watchFormData?.[`invoice_ndis_rate_${index}`],
    advanceServiceCategoryChanged,
  ]);
  const getAppliedAmount = () => {
    const hourlyRate = resolvedInvoiceNdisRate;
    const rateAmount = resolvedRateAmount;

    if (
      (hourlyRate === "" || hourlyRate == null) &&
      (rateAmount === "" || rateAmount == null)
    ) {
      return "";
    }

    return calculateAppliedPerParticipantRate(
      hourlyRate,
      rateAmount,
      shiftDetails,
      index,
      item,
      watchFormData?.[`ratio_${index}`],
      true
    );
    if (isNonBillable) return "0.00";
    return computeAppliedRateFromBillRate(resolvedRateAmount, shiftDetails);
  };
  const durationStringToMinutes = (durationStr) => {
    if (!durationStr) return 0;
    const [hours = 0, minutes = 0] = durationStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const calculateTotalBreakdownTime = () => {
    let totalMinutes = 0;
    for (let i = 0; i < totalBreakDown; i++) {
      const startTime = watchFormData[`start_time_${i}`];
      const endTime = watchFormData[`end_time_${i}`];
      if (startTime && endTime) {
        const hhmm = getBasicShiftDurationHHMM(startTime, endTime);
        if (!hhmm) continue;
        const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10) || 0);
        totalMinutes += h * 60 + m;
      }
    }

    return totalMinutes;
  };
  const validateTimeRange = (value, field = "start") => {
    if (useShiftWindowBounds && value) {
      if (field === "start") {
        const withinShift = validateAdvanceSegmentStartTime(
          shiftWindowStart,
          shiftWindowEnd,
          value
        );
        if (withinShift !== true) {
          return withinShift;
        }
      } else {
        const segStart =
          watchFormData[`start_time_${index}`] ?? shiftWindowStart;
        const withinShift = validateAdvanceSegmentEndTime(
          shiftWindowStart,
          shiftWindowEnd,
          segStart,
          value
        );
        if (withinShift !== true) {
          return withinShift;
        }
      }

      // Same participant: segments must not overlap (advance / V5 breakdowns).
      if (isAdvanceMode && shiftWindowStart) {
        const segments = [];
        for (let i = 0; i < totalBreakDown; i++) {
          const startOverride =
            i === index && field === "start" ? value : undefined;
          const endOverride = i === index && field === "end" ? value : undefined;
          segments.push({
            key: getBreakdownParticipantKey(
              shiftDetails,
              i,
              timesheetBreakdown?.[i] ?? (i === index ? item : null)
            ),
            start:
              startOverride !== undefined
                ? startOverride
                : watchFormData[`start_time_${i}`],
            end:
              endOverride !== undefined
                ? endOverride
                : watchFormData[`end_time_${i}`],
          });
        }
        const overlap = validateAdvanceSegmentNoOverlap(
          shiftWindowStart,
          segments,
          index,
          ADVANCE_SEGMENT_PARTICIPANT_OVERLAP_MSG
        );
        if (overlap !== true) {
          return overlap;
        }
      }
    }

    // Advance / legacy basic split: shift-window bounds already validate segment
    // times. Skip the old exact-sum check (stale row.duration caused false errors).
    if (isAdvanceMode || legacySplitShiftOnly || isSplitBillingMode) {
      return true;
    }

    const totalBreakdownMinutes = calculateTotalBreakdownTime();
    const totalDurationMinutes = durationStringToMinutes(totalItemsDuration);

    if (
      totalBreakdownMinutes > totalDurationMinutes ||
      totalBreakdownMinutes < totalDurationMinutes
    ) {
      const [hours = "00", minutes = "00"] = (totalItemsDuration || "").split(
        ":"
      );
      return `The total duration of all breakdowns combined must be exactly ${hours} hours and ${minutes} minutes in total.`;
    }

    return true;
  };

  const validateSegmentTimeChange = (value, field) => {
    const result = validateTimeRange(value, field);
    return result === true;
  };

  // Basic/legacy split times are editable in Code Allocation (same as advance).
  const lockBreakdownStart = false;
  const lockBreakdownEnd = false;

  const breakdownStartValue = watchFormData[`start_time_${index}`] ?? "";
  const breakdownEndValue = watchFormData[`end_time_${index}`] ?? "";
  const breakdownStartError =
    !lockBreakdownStart && breakdownStartValue
      ? (() => {
          const result = validateTimeRange(breakdownStartValue, "start");
          return result === true ? null : result;
        })()
      : null;
  const breakdownEndError =
    !lockBreakdownEnd && breakdownEndValue
      ? (() => {
          const result = validateTimeRange(breakdownEndValue, "end");
          return result === true ? null : result;
        })()
      : null;
  const breakdownSameTimeError =
    isAdvanceMode && breakdownStartValue && breakdownEndValue
      ? (() => {
          const result = validateAdvanceSplitEndTime(
            breakdownStartValue,
            breakdownEndValue
          );
          return result === true ? null : result;
        })()
      : null;

  const breakdownTimeRangeValidation = validateTimeRange();
  const breakdownTimeRangeError =
    breakdownTimeRangeValidation !== true
      ? breakdownTimeRangeValidation
      : null;

  const handleSleepoverToggle = useCallback(
    (value, idx) => {
      if (isAdvanceMode) {
        const participantKey = getBreakdownParticipantKey(
          shiftDetails,
          idx,
          timesheetBreakdown?.[idx] ?? (idx === index ? item : null)
        );

        if (value) {
          for (let i = 0; i < totalBreakDown; i++) {
            if (
              getBreakdownParticipantKey(
                shiftDetails,
                i,
                timesheetBreakdown?.[i] ?? (i === index ? item : null)
              ) === participantKey
            ) {
              setValue(`sleepover_${i}`, i === idx);
            }
          }
        } else {
          setValue(`sleepover_${idx}`, false);
        }
        return;
      }

      if (value) {
        setActiveSleepoverIndex(idx);
        for (let i = 0; i < totalBreakDown; i++) {
          setValue(`sleepover_${i}`, i === idx);
        }
      } else {
        setActiveSleepoverIndex(null);
        for (let i = 0; i < totalBreakDown; i++) {
          setValue(`sleepover_${i}`, false);
        }
      }
    },
    [isAdvanceMode, totalBreakDown, setValue, shiftDetails, timesheetBreakdown, index, item]
  );

  const showEditableSegmentSleepover =
    isSleepoverServiceCategory &&
    (isAdvanceMode ||
      Boolean(shiftDetails?.is_split_shift) ||
      Boolean(watchFormData?.sleepover));
  const showLegacySplitSleepoverReadonly = false;

  const isSleepoverToggleDisabled = useMemo(() => {
    if (isDisabled || !showEditableSegmentSleepover) return Boolean(isDisabled);

    if (!isAdvanceMode) {
      return (
        activeSleepoverIndex !== null && activeSleepoverIndex !== index
      );
    }

    if (parseBooleanVal(watchFormData?.[`sleepover_${index}`])) {
      return false;
    }

    const currentKey = getBreakdownParticipantKey(
      shiftDetails,
      index,
      item
    );

    for (let i = 0; i < totalBreakDown; i++) {
      if (i === index) continue;
      if (!parseBooleanVal(watchFormData?.[`sleepover_${i}`])) continue;
      const otherKey = getBreakdownParticipantKey(
        shiftDetails,
        i,
        timesheetBreakdown?.[i] ?? (i === index ? item : null)
      );
      if (otherKey === currentKey) return true;
    }

    return false;
  }, [
    isDisabled,
    showEditableSegmentSleepover,
    isAdvanceMode,
    activeSleepoverIndex,
    index,
    watchFormData,
    totalBreakDown,
    shiftDetails,
    item,
    timesheetBreakdown,
  ]);

  const segmentTotalTimeLabel = useMemo(() => {
    if (!isAdvanceMode) return null;

    const formStart = watchFormData?.[`start_time_${index}`];
    const formEnd = watchFormData?.[`end_time_${index}`];
    const allowSameClockFullDay =
      localBreakdownIndex == null || localBreakdownIndex === 1;

    if (formStart && formEnd) {
      const minutes = getAdvanceSegmentWindowMinutes(
        formStart,
        formEnd,
        shiftWindowStart,
        shiftWindowEnd,
        { allowSameClockFullDay }
      );
      if (minutes != null && minutes > 0) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${String(hours).padStart(2, "0")}:${String(mins).padStart(
          2,
          "0"
        )} total`;
      }
      if (
        isAdvanceSameClockTimeFullDay(formStart, formEnd) &&
        !isAdvanceSameClockTimeFullDay(shiftWindowStart, shiftWindowEnd)
      ) {
        return null;
      }
    }

    if (item?.duration) {
      const parts = String(item.duration).split(":");
      let h = (parts[0] || "00").padStart(2, "0");
      let m = (parts[1] || "00").padStart(2, "0");
      const isUnallocatedStub =
        formStart &&
        formEnd &&
        isAdvanceSameClockTimeFullDay(formStart, formEnd) &&
        !isAdvanceSameClockTimeFullDay(shiftWindowStart, shiftWindowEnd);
      if (isUnallocatedStub) return null;
      // Legacy stored "00:00:00" for a same-clock advance window → show 24:00.
      if (h === "00" && m === "00" && formStart && formEnd) {
        const minutes = getAdvanceSegmentWindowMinutes(
          formStart,
          formEnd,
          shiftWindowStart,
          shiftWindowEnd,
          { allowSameClockFullDay }
        );
        if (minutes === 24 * 60) {
          h = "24";
          m = "00";
        }
      }
      return `${h}:${m} total`;
    }

    return formatBillingDurationLabel(item?.duration);
  }, [
    isAdvanceMode,
    watchFormData,
    index,
    item?.duration,
    shiftWindowStart,
    shiftWindowEnd,
    localBreakdownIndex,
  ]);

  const handleAdvanceServiceCategoryChange = useCallback(
    (option) => {
      if (isStaShift) return;
      const newServiceTypeId = option?.value ?? null;
      setValue(`invoice_service_type_id_${index}`, newServiceTypeId);
      setValue(`invoice_item_id_${index}`, null);
      setValue(`invoice_ndis_rate_${index}`, "");
      setValue(`rate_amount_${index}`, "");
      setValue(`applied_rate_${index}`, "");
      // Active hours bill against the same catalogue — its line item and rate are stale too.
      setValue(`invoice_item_id_active_hours${index}`, null);
      setValue(`invoice_item_name_active_hours${index}`, "");
      setValue(`invoice_ndis_rate_active_hours${index}`, "");
      setValue(`rate_amount_active_hours${index}`, "");
      setValue(`applied_rate_active_hours${index}`, "");
      setSelectedNDISItem(null);
      setSelectedItemNumber(null);
      setAdvanceItemNumbers([]);
      setBudgetCheckResults([]);
      setSuccessfulBudgetChecks([]);
      clearErrors?.(`invoice_service_type_id_${index}`);
      clearErrors?.(`invoice_item_id_${index}`);
      clearErrors?.(`invoice_ndis_rate_${index}`);
      clearErrors?.(`rate_amount_${index}`);
      clearErrors?.(`applied_rate_${index}`);

      const categoryName = option?.name ?? option?.label;
      if (isAdvanceMode && !isSleepoverServiceCategoryName(categoryName)) {
        setValue(`sleepover_${index}`, false);
      }
    },
    [index, setValue, clearErrors, isAdvanceMode, isStaShift]
  );

  const ndisLineItemOptions = effectiveItemNumbers;
  const ndisLineItemDisabled =
    disabledAllComponent ||
    isDisabled ||
    ((isAdvanceMode || legacySplitShiftOnly) &&
      !breakdownServiceCategory?.value);

  const sleepoverWatchKey = (() => {
    const parts = [];
    for (let i = 0; i < totalBreakDown; i++) {
      parts.push(
        String(Boolean(watchFormData?.[`sleepover_${i}`])),
        watchFormData?.[`start_time_${i}`] ?? "",
        watchFormData?.[`end_time_${i}`] ?? ""
      );
    }
    return parts.join("|");
  })();

  useEffect(() => {
    if (index !== 0) return;
    if (isSplitBillingMode) {
      return;
    }
    if (!isAdvanceMode && !watchFormData?.sleepover) {
      return;
    }
    if (isAdvanceMode) {
      const apiSleepoverConfigured = (timesheetBreakdown ?? []).some((row) =>
        parseBooleanVal(row?.sleepover ?? row?.is_sleepover)
      );
      const formSleepoverConfigured = Array.from({ length: totalBreakDown }).some(
        (_, i) => parseBooleanVal(watchFormData?.[`sleepover_${i}`])
      );
      if (apiSleepoverConfigured || formSleepoverConfigured) {
        return;
      }
    }
    let matchIdx = -1;
    for (let i = 0; i < totalBreakDown; i++) {
      const s = watchFormData[`start_time_${i}`];
      const e = watchFormData[`end_time_${i}`];
      if (!isSleepoverBreakdownTimeRange(s, e)) continue;
      const segmentCategoryName = isAdvanceMode
        ? serviceTypes?.find(
            (st) =>
              String(st.value) ===
              String(watchFormData[`invoice_service_type_id_${i}`])
          )?.name
        : serviceCategory?.name;
      if (!isSleepoverServiceCategoryName(segmentCategoryName)) continue;
      matchIdx = i;
      break;
    }
    if (matchIdx < 0) return;

    const currentActive = Array.from({ length: totalBreakDown }).findIndex(
      (_, i) => watchFormData[`sleepover_${i}`]
    );
    if (currentActive === matchIdx) return;

    handleSleepoverToggle(true, matchIdx);
  }, [
    index,
    sleepoverWatchKey,
    totalBreakDown,
    isSplitBillingMode,
    handleSleepoverToggle,
    serviceTypes,
    serviceCategory?.name,
    isAdvanceMode,
    watchFormData?.sleepover,
    timesheetBreakdown,
  ]);

  useEffect(() => {
    const activeIndex = Array.from({ length: totalBreakDown }).findIndex(
      (_, i) => watchFormData[`sleepover_${i}`]
    );
    setActiveSleepoverIndex((prev) => {
      const next = activeIndex >= 0 ? activeIndex : null;
      return prev === next ? prev : next;
    });
  }, [sleepoverWatchKey, totalBreakDown]);

  const [budgetCheckResults, setBudgetCheckResults] = useState([]);
  const [successfulBudgetChecks, setSuccessfulBudgetChecks] = useState([]);
  const [isCheckingBudget, setIsCheckingBudget] = useState(false);
  const budgetCheckAttemptedRef = React.useRef(false);
  const lastBudgetCheckItemIdRef = React.useRef(null);
  const budgetCheckRequestIdRef = React.useRef(0);
  const isApprovedForSnapshot =
    String(shiftDetails?.status || "").toLowerCase() === "approved" ||
    String(shiftDetails?.progress || "").toLowerCase() === "pay_approved";

  const checkPlanBudget = async (
    ndisItemId,
    participantId,
    participantName,
    invoiceTimesheetId = null,
    invoiceTimesheetUuid = null
  ) => {
    const customLineItemUuid =
      typeof ndisItemId === "string" && ndisItemId.startsWith("custom:")
        ? ndisItemId.replace("custom:", "")
        : null;

    if (
      !spSettings?.link_rostering_core_funding ||
      !ndisItemId ||
      !participantId
    ) {
      return { success: true };
    }

    const payload = {
      price_catalog_id: ndisItemId,
      custom_line_item_uuid: customLineItemUuid,
      pt_id: participantId,
      timesheet_date: shiftDetails?.time_sheet_date ?? null,
      timesheet_id: shiftDetails?.id ?? null,
      timesheet_uuid: shiftDetails?.uuid ?? null,
      invoice_timesheet_id: invoiceTimesheetId,
      invoice_timesheet_uuid: invoiceTimesheetUuid,
      prefer_snapshot: isApprovedForSnapshot,
    };

    try {
      const response = await apiPostRequest(
        URL_ROSTER_LINK_TO_CORE_FUNDING,
        payload
      );

      if (response?.status === 200) {
        const resultMap = response?.data?.data?.items?.results || {};
        const raw =
          resultMap?.[participantId] ?? resultMap?.[String(participantId)];
        if (!raw) {
          const errors = response?.data?.data?.items?.errors;
          return {
            success: false,
            participantName,
            participantId,
            error:
              (Array.isArray(errors) && errors.length ? errors.join(" ") : null) ||
              "No linked funding found for this participant and line item.",
          };
        }
        return {
          success: true,
          participantName,
          data: raw?.ssb ?? raw,
          funding: {
            contract_id: raw?.contract_id ?? null,
            start_date: raw?.start_date ?? null,
            end_date: raw?.end_date ?? null,
          },
        };
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Budget check failed";
      const errorResult = {
        success: false,
        participantName,
        participantId,
        error: errorMessage,
      };
      return errorResult;
    }
  };

  const formatParticipantName = (str) => {
    return (
      str
        ?.split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ") || ""
    );
  };

  useEffect(() => {
    if (!isOpen) {
      budgetCheckAttemptedRef.current = false;
      lastBudgetCheckItemIdRef.current = null;
      return;
    }

    const currentItemId = watchFormData[`invoice_item_id_${index}`] ?? null;
    const checkKey = `${shiftDetails?.uuid ?? ""}|${currentItemId ?? ""}|${
      suggestedItemNumber?.id ?? ""
    }`;
    if (lastBudgetCheckItemIdRef.current !== checkKey) {
      lastBudgetCheckItemIdRef.current = checkKey;
      budgetCheckAttemptedRef.current = false;
    }

    // Do not depend on isCheckingBudget - toggling it re-entered this effect
    // and re-fired link-rostering-with-core-funding in a loop.
    if (budgetCheckAttemptedRef.current) {
      return;
    }

    const isCustomLineItemId = (id) =>
      typeof id === "string" && id.startsWith("custom:");

    let itemToCheck = currentItemId;
    if (!currentItemId && suggestedItemNumber?.id) {
      itemToCheck = suggestedItemNumber.id;
    }

    if (
      !breakdownServiceCategory?.name ||
      (!isCustomLineItemId(itemToCheck) &&
        !CORE_CATEGORIES?.includes(breakdownServiceCategory?.name))
    ) {
      return;
    }

    if (!billingParticipants || billingParticipants.length === 0) {
      return;
    }

    if (!itemToCheck) {
      return;
    }

    if (!spSettings?.link_rostering_core_funding) {
      return;
    }

    const optionsSource =
      ndisLineItemOptions?.length > 0 ? ndisLineItemOptions : itemNumber;
    const selectedInOptions = optionsSource?.find(
      (opt) => String(opt?.value) === String(itemToCheck)
    );
    const suggestedMatches =
      suggestedItemNumber?.id != null &&
      String(suggestedItemNumber.id) === String(itemToCheck);
    // Wait until options (or suggested selection) confirm the item — retry via
    // itemNumber / suggestedItemNumber deps when they arrive later.
    if (
      !selectedInOptions &&
      !suggestedMatches &&
      !isCustomLineItemId(itemToCheck)
    ) {
      return;
    }
    if (
      !selectedInOptions &&
      !suggestedMatches &&
      isCustomLineItemId(itemToCheck) &&
      (optionsSource?.length ?? 0) === 0 &&
      !suggestedItemNumber?.id
    ) {
      return;
    }

    budgetCheckAttemptedRef.current = true;
    const requestId = ++budgetCheckRequestIdRef.current;

    const checkInitialSelectedItem = async () => {
      setIsCheckingBudget(true);
      const checkResults = [];

      try {
        if (billingParticipants.length > 1) {
          const checkPromises = billingParticipants.map((participant) => {
            if (participant?.id) {
              const participantName = `${participant?.first_name || ""} ${
                participant?.last_name || ""
              }`.trim();
              return checkPlanBudget(
                itemToCheck,
                participant?.id,
                participantName,
                item?.id ?? null,
                item?.uuid ?? null
              );
            }
            return Promise.resolve({ success: true });
          });
          const results = await Promise.all(checkPromises);
          checkResults.push(...results.filter((r) => r !== null));
        } else {
          const participant = billingParticipants[0];
          if (participant?.id) {
            const participantName = `${participant?.first_name || ""} ${
              participant?.last_name || ""
            }`.trim();
            const result = await checkPlanBudget(
              itemToCheck,
              participant?.id,
              participantName,
              item?.id ?? null,
              item?.uuid ?? null
            );
            checkResults.push(result);
          }
        }

        if (budgetCheckRequestIdRef.current !== requestId) {
          return;
        }

        const failedChecks = checkResults.filter((r) => !r?.success);
        const passedChecks = checkResults.filter((r) => r?.success && r?.data);

        setBudgetCheckResults(failedChecks);
        setSuccessfulBudgetChecks(passedChecks);
      } finally {
        if (budgetCheckRequestIdRef.current === requestId) {
          setIsCheckingBudget(false);
        }
      }
    };

    checkInitialSelectedItem();
  }, [
    isOpen,
    watchFormData[`invoice_item_id_${index}`],
    suggestedItemNumber?.id,
    breakdownServiceCategory?.name,
    itemNumber,
    ndisLineItemOptions,
    billingParticipants,
    shiftDetails?.uuid,
    spSettings?.link_rostering_core_funding,
  ]);

  const getDateForBreakdown = () => {
    const segStart = watchFormData[`start_time_${index}`];
    const segEnd = watchFormData[`end_time_${index}`];
    const itemStartDate = datePartFromDateTime(item?.start_time);
    const itemEndDate = datePartFromDateTime(item?.end_time);
    const formMatchesItem =
      clocksMatchDateTime(segStart, item?.start_time) &&
      clocksMatchDateTime(segEnd, item?.end_time);

    let startDate = itemStartDate;
    let endDate = itemEndDate;

    if (formMatchesItem && startDate) {
      if (isExclusiveMidnightEnd(segStart, segEnd)) {
        endDate = startDate;
      } else if (!endDate) {
        endDate = startDate;
      }
    } else {
      const rows = timesheetBreakdown || [];
      const currentKey = getBreakdownParticipantKey(shiftDetails, index, item);
      const canGroupByParticipant =
        Boolean(currentKey) && !String(currentKey).startsWith("segment-");

      startDate =
        datePartFromDateTime(startTime) ||
        (startTime ? moment(startTime).format("YYYY-MM-DD") : itemStartDate);

      for (let i = 0; i < index; i++) {
        if (canGroupByParticipant) {
          const rowKey = getBreakdownParticipantKey(
            shiftDetails,
            i,
            rows[i]
          );
          if (rowKey !== currentKey) continue;
        }
        const isNextDay =
          isCheckNextDayShift(
            startTime,
            watchFormData[`start_time_${i}`],
            watchFormData[`end_time_${i}`]
          ) !== false;
        if (isNextDay) {
          startDate = moment(startDate).add(1, "days").format("YYYY-MM-DD");
        }
      }

      const endGoesNextDay =
        isEndTimeNextDay(startDate, segStart, segEnd) &&
        !isExclusiveMidnightEnd(segStart, segEnd);
      endDate = moment(startDate)
        .add(endGoesNextDay ? 1 : 0, "days")
        .format("YYYY-MM-DD");
    }

    if (!startDate) return "";
    const displayEnd = endDate || startDate;
    return `${moment(startDate).format("DD/MM/YYYY")} - ${moment(
      displayEnd
    ).format("DD/MM/YYYY")}`;
  };



  const headerHasTimeError =
    watchFormData[`start_time_${index}`] === undefined ||
    watchFormData[`start_time_${index}`] === null ||
    watchFormData[`start_time_${index}`] === "";

  const breakdownTitle =
    localBreakdownTotal != null && localBreakdownTotal > 1
      ? `Breakdown ${localBreakdownIndex} of ${localBreakdownTotal}`
      : localBreakdownIndex != null
      ? `Breakdown ${localBreakdownIndex}`
      : `Breakdown ${index + 1}`;

  const headerRatio =
    watchFormData?.[`ratio_${index}`] || item?.ratio || shiftDetails?.ratio;

  return (
    <div className="ca-breakdown-item">
      <div className="ca-breakdown-header-row">
        <button
          type="button"
          onClick={() => {
            if (!isDisabled) setIsOpen(!isOpen);
          }}
          title={isDisabled ? "Timesheet can not be edit" : ""}
          disabled={isDisabled}
          className={`ca-breakdown-header ${
            isDisabled ? "is-disabled" : ""
          } ${headerHasTimeError ? "is-error" : ""} ${
            isOpen ? "is-open" : ""
          }`}
        >
          <div className="ca-breakdown-header-main">
            <div className="ca-breakdown-header-left">
              <TimesheetCodeAllocationBreakdownIcon />
              <span className="ca-breakdown-index">{breakdownTitle}</span>
              {headerRatio ? (
                <span className="ca-participant-chip">Ratio {headerRatio}</span>
              ) : null}
              {segmentTotalTimeLabel ? (
                <span className="ca-breakdown-index-duration">
                  {segmentTotalTimeLabel}
                </span>
              ) : null}
              {breakdownClaimTypeBadge && (
                <span className="ca-breakdown-travel-badge">
                  {breakdownClaimTypeBadge}
                </span>
              )}
            </div>
            <div className="ca-breakdown-meta">
              <span className="ca-breakdown-meta-date">{getDateForBreakdown()}</span>
              <span className="ca-breakdown-meta-time">
                {watchFormData && watchFormData[`start_time_${index}`]
                  ? moment(watchFormData[`start_time_${index}`], "HH:mm").format(
                      "hh:mm A"
                    )
                  : moment.utc(item?.start_time).format("hh:mm A")}{" "}
                –{" "}
                {watchFormData && watchFormData[`end_time_${index}`]
                  ? moment(watchFormData[`end_time_${index}`], "HH:mm").format(
                      "hh:mm A"
                    )
                  : moment.utc(item?.end_time).format("hh:mm A")}
              </span>
            </div>
          </div>
          <HiOutlineChevronDown
            className={`ca-breakdown-chevron ${isOpen ? "is-open" : ""}`}
          />
        </button>
        {canRemove && (
          <div className="ca-breakdown-remove">
            <AdvanceModeParticipantRemoveButton
              onClick={onRemove}
              disabled={isDisabled}
              title="Remove breakdown"
            />
          </div>
        )}
      </div>

        {isOpen && (
          <div className='ca-breakdown-body'>
            <div className='flex flex-col gap-4 w-full'>
              <div className='flex flex-col gap-3 flex-1'>
                <div className='flex items-center justify-between gap-2'>
                  <label className='ca-field-label--dark'>
                    Schedule Start/Finish
                  </label>
                  {useShiftWindowBounds &&
                    shiftWindowStart &&
                    shiftWindowEnd && (
                    <span className='text-xs text-[#64748B]'>
                      Within shift{" "}
                      {moment(shiftWindowStart, "HH:mm").format("hh:mm A")} –{" "}
                      {moment(shiftWindowEnd, "HH:mm").format("hh:mm A")}
                    </span>
                  )}
                  {showLegacySplitSleepoverReadonly && (
                      <>
                        <div className='flex items-center gap-x-2'>
                          <label className=' text-sm font-semibold mb-1'>
                            Sleepover
                          </label>
                          <div>
                            <Controller
                              name={`sleepover_${index}`}
                              control={control}
                              defaultValue={false || item?.sleepover}
                              render={({ field }) => (
                                <ToggleSwitch
                                  {...field}
                                  isSmall
                                  disabled
                                  onChange={(value) => {
                                    field.onChange(value);
                                    handleSleepoverToggle(value, index);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {showEditableSegmentSleepover && (
                        <div className='flex items-center gap-x-2'>
                          <label className='text-sm font-semibold mb-1'>
                            Sleepover
                          </label>
                          <div>
                            <Controller
                              name={`sleepover_${index}`}
                              control={control}
                              defaultValue={Boolean(item?.sleepover)}
                              render={({ field }) => (
                                <ToggleSwitch
                                  {...field}
                                  isSmall
                                  disabled={isSleepoverToggleDisabled}
                                  onChange={(value) => {
                                    field.onChange(value);
                                    handleSleepoverToggle(value, index);
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                      )}

                    {shiftDetails?.is_split_shift &&
                      showTravelFromBusinessSettings && (
                        <div className='flex items-center gap-x-2'>
                          <label className='text-sm font-semibold mb-1'>
                            Travel
                          </label>
                          <ToggleSwitch
                            value={isTravelBreakdown}
                            isSmall
                            disabled={isDisabled}
                          />
                        </div>
                      )}

                     
                </div>
                <div className='advance-mode-root w-full'>
                  <div className='advance-mode-panel'>
                    <AdvanceModeTimeRange
                      startField={
                        <>
                          <span className='mb-1 block text-[10px] font-medium text-slate-500'>
                            Start
                          </span>
                          <div
                            style={{
                              pointerEvents: lockBreakdownStart ? "none" : "auto",
                              opacity: lockBreakdownStart || isDisabled ? 0.6 : 1,
                            }}
                          >
                            <input
                              type='time'
                              disabled={isDisabled || lockBreakdownStart}
                              {...register(`start_time_${index}`, {
                                required: "Start time is required",
                                validate: (value) =>
                                  !lockBreakdownStart
                                    ? validateTimeRange(value, "start")
                                    : true,
                              })}
                              value={breakdownStartValue}
                              onChange={(e) => {
                                if (lockBreakdownStart) return;
                                setValue(`start_time_${index}`, e.target.value, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }}
                              className={ADVANCE_TIME_INPUT_CLASS}
                            />
                            <AdvanceModeFieldError
                              message={
                                !breakdownSameTimeError ? breakdownStartError : null
                              }
                            />
                          </div>
                        </>
                      }
                      endField={
                        <>
                          <span className='mb-1 block text-[10px] font-medium text-slate-500'>
                            End
                          </span>
                          <div
                            style={{
                              pointerEvents: lockBreakdownEnd ? "none" : "auto",
                              opacity: lockBreakdownEnd || isDisabled ? 0.6 : 1,
                            }}
                          >
                            <input
                              type='time'
                              disabled={isDisabled || lockBreakdownEnd}
                              {...register(`end_time_${index}`, {
                                required: "End time is required",
                                validate: (value) => {
                                  const timeValidation = !lockBreakdownEnd
                                    ? validateTimeRange(value, "end")
                                    : true;
                                  if (timeValidation !== true) {
                                    return timeValidation;
                                  }

                                  const startVal =
                                    watchFormData[`start_time_${index}`];
                                  if (isAdvanceMode) {
                                    if (
                                      isAdvanceShiftZeroDuration(startVal, value)
                                    ) {
                                      return "Shift can't be zero hours & minutes!";
                                    }
                                    return true;
                                  }

                                  const shiftLengthValidation = getShiftLength(
                                    startVal,
                                    value,
                                    false,
                                    true
                                  );
                                  if (shiftLengthValidation === "00:00") {
                                    return "Shift can't be zero hours & minutes!";
                                  }

                                  return true;
                                },
                              })}
                              value={breakdownEndValue}
                              onChange={(e) => {
                                if (lockBreakdownEnd) return;
                                const { value } = e.target;
                                setValue(`end_time_${index}`, value, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });

                                // Chain next same-participant segment start when end time changes.
                                if (validateSegmentTimeChange(value, "end")) {
                                  const currentKey = getBreakdownParticipantKey(
                                    shiftDetails,
                                    index,
                                    timesheetBreakdown?.[index] ?? item
                                  );
                                  for (let i = index + 1; i < totalBreakDown; i += 1) {
                                    const nextKey = getBreakdownParticipantKey(
                                      shiftDetails,
                                      i,
                                      timesheetBreakdown?.[i] ??
                                        (i === index ? item : null)
                                    );
                                    if (nextKey === currentKey) {
                                      setValue(`start_time_${i}`, value, {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                      });
                                      break;
                                    }
                                  }
                                }
                              }}
                              className={ADVANCE_TIME_INPUT_CLASS}
                            />
                            <AdvanceModeFieldError
                              message={
                                !breakdownSameTimeError ? breakdownEndError : null
                              }
                            />
                          </div>
                        </>
                      }
                    />
                    <AdvanceModeTimeRangeSharedError
                      message={breakdownSameTimeError}
                    />
                    {!isAdvanceMode &&
                      !lockBreakdownStart &&
                      !lockBreakdownEnd &&
                      breakdownTimeRangeError && (
                        <p className='text-red-600 text-sm mt-1'>
                          {breakdownTimeRangeError}
                        </p>
                      )}
                  </div>
                </div>
                {(isAdvanceMode || shiftDetails?.is_split_shift) && (
                  <div>
                    <label className='ca-field-label--dark'>
                      Service Category <span className='text-red-500'>*</span>
                    </label>
                    <SelectOption
                      isSearch={true}
                      useMenuPortal={true}
                      data={serviceTypes}
                      name={`invoice_service_type_id_${index}`}
                      isRequired={true}
                      register={register}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      cssClass='text-gray-700'
                      selectedOption={breakdownServiceCategory}
                      persistSelectedOption={true}
                      preserveSelectionWhenOptionMissing={true}
                      isDisabled={
                        isDisabled || disabledAllComponent || isStaShift
                      }
                      handleChange={handleAdvanceServiceCategoryChange}
                    />
                  </div>
                )}
                <label className='ca-field-label--dark'>
                  {lineItemLabel} name <span className='text-red-500'>*</span>
                </label>
                <SelectOption
                  key={
                    isAdvanceMode || legacySplitShiftOnly
                      ? `ndis-item-${index}-${breakdownServiceCategory?.value ?? "none"}`
                      : `ndis-item-${index}`
                  }
                  useMenuPortal={true}
                  data={ndisLineItemOptions}
                  isDisabled={ndisLineItemDisabled}
                  loading={
                    (isAdvanceMode || legacySplitShiftOnly) && advanceItemLoading
                  }
                  name={`invoice_item_id_${index}`}
                  isRequired={true}
                  isSearch={true}
                  register={register}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  cssClass='text-gray-700'
                  selectedOption={selectedLineItemOption ?? null}
                  persistSelectedOption={true}
                  preserveSelectionWhenOptionMissing={true}                  handleChange={async (selectedItem) => {
                    const withNumber = selectedItem
                      ? {
                          ...selectedItem,
                          number:
                            resolveBreakdownSupportItemNumber(selectedItem, item) ||
                            selectedItem.number ||
                            "",
                        }
                      : null;
                    setSelectedNDISItem(withNumber);
                    setSelectedItemNumber(withNumber);
                    if (withNumber?.value != null) {
                      setValue(`invoice_item_id_${index}`, withNumber.value);
                    }
                    setValue(
                      `invoice_item_name_${index}`,
                      withNumber?.number || ""
                    );
                    // Picking an item re-suggests its catalogue rate; the user can
                    // still switch to an alternate rate afterwards.
                    if (withNumber?.price != null) {
                      setValue(`invoice_ndis_rate_${index}`, withNumber.price);
                      setValue(`rate_amount_${index}`, withNumber.price);
                    }
                    if (isAdvanceMode && isStaShift) {
                      setValue(
                        `ratio_${index}`,
                        resolveRatioFromLineItemOption(withNumber)
                      );
                    } else {
                      const itemRatio =
                        withNumber?.ratio ||
                        extractRatioFromLineItemLabel(
                          withNumber?.name || withNumber?.label
                        );
                      if (itemRatio) {
                        setValue(`ratio_${index}`, itemRatio);
                      }
                    }
                    setBudgetCheckResults([]);
                    setSuccessfulBudgetChecks([]);
                    const isCustomSelected =
                      typeof withNumber?.value === "string" &&
                      withNumber.value.startsWith("custom:");
                    if (
                      !breakdownServiceCategory?.name ||
                      (!isCustomSelected &&
                        !CORE_CATEGORIES?.includes(
                          breakdownServiceCategory?.name
                        ))
                    ) {
                      return;
                    }
                    if (!billingParticipants || billingParticipants.length === 0) {
                      return;
                    }
                    setIsCheckingBudget(true);
                    const checkResults = [];
                    const successfulResults = [];
                    if (billingParticipants.length > 1) {
                      const checkPromises = billingParticipants.map(
                        (participant) => {
                          if (participant?.id) {
                            const participantName = `${
                              participant?.first_name || ""
                            } ${participant?.last_name || ""}`.trim();
                            return checkPlanBudget(
                              withNumber?.value,
                              participant?.id,
                              participantName,
                              item?.id ?? null,
                              item?.uuid ?? null
                            );
                          }
                          return Promise.resolve({ success: true });
                        }
                      );
                      const results = await Promise.all(checkPromises);
                      checkResults.push(...results.filter((r) => r !== null));
                    } else {
                      const participant = billingParticipants[0];
                      if (participant?.id) {
                        const participantName = `${
                          participant?.first_name || ""
                        } ${participant?.last_name || ""}`.trim();
                        const result = await checkPlanBudget(
                          withNumber?.value,
                          participant?.id,
                          participantName,
                          item?.id ?? null,
                          item?.uuid ?? null
                        );
                        checkResults.push(result);
                      }
                    }
                    const failedChecks = checkResults.filter(
                      (r) => !r?.success
                    );
                    const passedChecks = checkResults.filter(
                      (r) => r?.success && r?.data
                    );
                    setBudgetCheckResults(failedChecks);
                    setSuccessfulBudgetChecks(passedChecks);
                    if (failedChecks.length > 0) {
                      const uniqueErrors = [
                        ...new Set(failedChecks.map((f) => f.error)),
                      ];
                      if (failedChecks.length === 1) {
                        toast.error(failedChecks[0].error);
                      } else {
                        const affectedParticipants = failedChecks
                          .map((f) => formatParticipantName(f.participantName))
                          .join(", ");
                        toast.error(
                          `Budget check failed for ${participantLabelPlural.toLowerCase()}: ${affectedParticipants}`
                        );
                        failedChecks.forEach((check) => {
                          //   console.error(`Budget check failed for ${check.participantName}:`, check.error);
                        });
                      }
                    }
                    setIsCheckingBudget(false);
                  }}
                />
                {shiftDetails?.is_split_shift &&
                  showTravelFromBusinessSettings &&
                  isTravelBreakdown &&
                  item?.ndis_support_item_name && (
                    <p className='text-xs text-[#0F766E] mt-1 font-medium'>
                      {`${capitalizeFirstLetter(
                        String(item.ndis_support_item_name).trim()
                      )} - Travel`}
                    </p>
                  )}
                {isCheckingBudget && (
                  <p className='ca-budget-checking'>
                    Checking budget availability...
                  </p>
                )}

                {budgetCheckResults.length > 0 && (
                  <div className='ca-budget-error'>
                    <p className='ca-budget-error-title'>Budget Check Issues:</p>
                    <ul className='ca-budget-error-list'>
                      {budgetCheckResults.map((result, idx) => (
                        <li key={idx}>
                          <strong>
                            {formatParticipantName(result?.participantName)}:
                          </strong>{' '}
                          {result?.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {successfulBudgetChecks.length > 0 && (
                  <div className='ca-budget-success'>
                    <div className='ca-budget-success-text space-y-2'>
                      {successfulBudgetChecks.map((result, idx) => (
                        <div
                          key={idx}
                          className='border-b border-green-100 pb-2 last:border-0'
                        >
                          <p className='font-medium'>
                            {formatParticipantName(result?.participantName)}
                            {result?.funding?.contract_id && (
                              <span className='font-normal'>
                                {`'s funding id # `}
                                <strong>{result.funding.contract_id}</strong>
                              </span>
                            )}
                          </p>

                          <div className='text-xs space-y-1 mt-1'>
                            <p>
                              Remaining Budget: $
                              {parseFloat(
                                result?.data?.remain_amount || "0"
                              ).toFixed(2)}
                            </p>

                            {result?.data?.unit === "E" ? (
                              <p>
                              Remaining Quantity:{" "}
                                {result.data?.remain_quantity}
                              </p>
                            ) : (
                              <p>
                              Remaining Time:{" "}
                                {result?.data?.remain_total_hours || 0}h{" "}
                                {result?.data?.remain_total_mint || 0}m
                              </p>
                            )}
                            {/* <p>╬ô├ç├│ Remaining Time: {result.data?.remain_total_hours || 0}h {result.data?.remain_total_mint || 0}m</p> */}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
               

                <div className='ca-breakdown-fields-grid ca-breakdown-fields-grid--2'>
                  {isAdvanceMode && (
                    <div className='flex flex-col gap-2 flex-1'>
                      <label className='ca-field-label--dark'>Ratio</label>
                      <Controller
                        name={`ratio_${index}`}
                        control={control}
                        defaultValue={DEFAULT_ADVANCE_RATIO}
                        render={({ field }) => (
                          <AdvanceModeRatioSelect
                            value={field.value || DEFAULT_ADVANCE_RATIO}
                            onChange={(ratio) => {
                              field.onChange(ratio || DEFAULT_ADVANCE_RATIO);
                            }}
                            disabled={
                              isDisabled ||
                              isNonBillable ||
                              disabledAllComponent ||
                              isStaRatioLocked
                            }
                          />
                        )}
                      />
                    </div>
                  )}
                  <div className='flex flex-col gap-2 flex-1'>
                    <label className='ca-field-label--dark'>
                      {spSettings?.care_sector === "aged_care"
                        ? "Service type number"
                        : "Support item number"}
                    </label>
                    <InputField
                      isDisabled={true}
                      readOnly={true}
                      cssClass='mt-0 h-40-important text-gray-700'
                      inputWidth='w-full'
                      backgroundColor='#F3F4F6 '
                      name={`invoice_item_name_${index}`}
                      value={resolvedSupportItemNumber}
                      setValue={setValue}
                    />
                  </div>
                  <div className='flex flex-col gap-2 flex-1'>
                    <label className='ca-field-label'>Hourly Rate</label>
                    <Controller
                      name={`invoice_ndis_rate_${index}`}
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <>
                          <Select
                            options={NDIS_RATE}
                            value={getHourlyRateSelectValue(field.value)}
                            onChange={(option) => {
                              const rateValue = option?.value ?? null;
                              if (rateValue === field.value) return;

                              field.onChange(rateValue);
                              if (rateValue === "alternate") {
                                // Basic/legacy split: reset to 0 for a fresh custom amount.
                                if (legacySplitShiftOnly) {
                                  setValue(`rate_amount_${index}`, 0);
                                } else {
                                  const amount =
                                    watchFormData?.[`rate_amount_${index}`];
                                  if (
                                    amount === undefined ||
                                    amount === null ||
                                    amount === ""
                                  ) {
                                    syncRateAmountFromHourlyRate(rateValue);
                                  }
                                }
                              } else {
                                syncRateAmountFromHourlyRate(rateValue);
                              }
                              if (clearErrors) {
                                clearErrors(`invoice_ndis_rate_${index}`);
                              }
                            }}
                            isDisabled={
                              isNonBillable || disabledAllComponent
                            }
                            menuPortalTarget={document.body}
                            menuPlacement='auto'
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 10010,
                              }),
                              control: (base) => ({
                                ...base,
                                fontSize: "12px",
                              }),
                              singleValue: (base) => ({
                                ...base,
                                fontSize: "12px",
                              }),
                            }}
                            className='w-full roleSelector'
                            getOptionLabel={(option) =>
                              capitalizeFirstLetter(option.label)
                            }
                          />
                          {errors[`invoice_ndis_rate_${index}`]?.type ===
                            "required" && (
                            <span
                              style={{
                                fontSize: "13px",
                                color: "red",
                                marginLeft: "5px",
                              }}
                            >
                              This field is required!
                            </span>
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className='flex flex-col gap-3 flex-1'>
                <div className='ca-breakdown-fields-grid ca-breakdown-fields-grid--2'>
                  <div className='flex flex-col gap-2 flex-1'>
                    <label className='ca-field-label'>
                      Rate Amount <span className='text-red-500'>*</span>
                    </label>
                    <div className='w-full'>
                      {resolvedInvoiceNdisRate === "alternate" ? (
                        <InputField
                          key={`rate_amount_${index}_alternate`}
                          isDisabled={
                            watchFormData?.non_billable || disabledAllComponent
                          }
                          cssClass='mt-0 h-40-important text-gray-700'
                          inputWidth='w-full'
                          setValue={setValue}
                          value={resolvedRateAmount}
                          name={`rate_amount_${index}`}
                          isRequired={true}
                          register={register}
                          type='number'
                          step='0.01'
                          backgroundColor='#F3F4F6 '
                        />
                      ) : (
                        <InputField
                          key={`rate_amount_${index}_catalog_${String(
                            resolvedRateAmount ?? resolvedInvoiceNdisRate ?? ""
                          )}`}
                          type='number'
                          isDisabled={true || disabledAllComponent}
                          readOnly={true}
                          cssClass='mt-0 h-40-important text-gray-700'
                          inputWidth='w-full'
                          name={`rate_amount_${index}`}
                          isRequired={true}
                          value={resolvedRateAmount}
                          backgroundColor='#F3F4F6 '
                        />
                      )}
                    </div>
                  </div>
                  <div className='flex flex-col gap-2 flex-1'>
                    <label className='ca-field-label'>
                      Applied Rate <span className='text-red-500'>*</span>
                    </label>
                    <InputField
                      isDisabled={true || disabledAllComponent}
                      cssClass='mt-0 h-40-important text-gray-700'
                      inputWidth='w-full'
                      name={`applied_rate_${index}`}
                      value={isNonBillable ? 0 : getAppliedAmount()}
                      backgroundColor='#F3F4F6 '
                    />
                  </div>
                </div>
               
                {((showLegacySplitSleepoverReadonly && item?.sleepover) ||
                  (showEditableSegmentSleepover &&
                    watchFormData?.[`sleepover_${index}`])) && (
                    <ActiveHours
                      register={register}
                      watchFormData={watchFormData}
                      index={index}
                      item={item}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      itemNumber={
                        isAdvanceMode
                          ? ndisLineItemOptions
                          : isBasicSplitSleepoverAutoOn
                            ? basicSplitActiveHoursItemNumbers
                            : itemNumber
                      }
                      suggestedItemNumber={suggestedItemNumber}
                      NDIS_RATE={NDIS_RATE}
                      getAppliedAmount={getAppliedAmount}
                      useExactAppliedRate={true}
                      selectedTimeSheet={shiftDetails}
                      disabledAllComponent={disabledAllComponent}
                      isNonBillable={isNonBillable}
                      isEditMode={true}
                      totalBreakdownLength={calculateTotalHoursAndMinutes(
                        watchFormData[`start_time_${index}`],
                        watchFormData[`end_time_${index}`]
                      )}
                      errors={errors}
                      // setError={setError}
                      alreadySelectedItemNumber={
                        watchFormData[`invoice_item_id_${index}`]
                      }
                      serviceCategory={serviceCategory}
                      spSettings={spSettings}
                    />
                  )}
                {allowSplit && (
                  <AdvanceModeSplitSegmentButton
                    onClick={onSplit}
                    disabled={isDisabled}
                  />
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
